/**
 * MoonTreatmentHubCard
 *
 * Unified Treatment Hub for the moon app. EMBER glass-styled.
 * Full parity with olly's TreatmentHubCard, using shared package hooks.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { View, Pressable, Platform, Image } from "react-native";
import Animated, { FadeIn, ZoomIn } from "react-native-reanimated";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Calendar,
  CalendarPlus,
  Check,
  FlaskConical,
  ListChecks,
  Lock,
  Pencil,
  Pill,
  Play,
  ChevronRight,
  Plus,
  RotateCcw,
  SkipForward,
  Snowflake,
} from "lucide-react-native";
import type {
  TreatmentStepProgress,
  LocalMedication,
  TreatmentPhaseName,
  CycleType,
} from "@louise/care/treatment-phases";
import {
  isStepPassedUnfilled,
  resolveJourneyPointer,
} from "@src/packages/cycles/presenters/journeyPointer";
import {
  TRIGGER_STEP_ID,
  recordedMedicationRole,
  resolveTriggerStepPhaseId,
  selectStepMedications,
} from "@louise/care/medications/stepMedications";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  usePhases,
  useCreatePhase,
  getPhaseTypeForStep,
} from "@src/packages/patients/hooks/usePhases";
import {
  migrateEggFreezingStepProgress,
  StepWizardContent,
  stepUsesDetailsModal,
  type TreatmentStepConfig,
  type StepPerspective,
} from "@src/packages/homepage/sections/TreatmentProgressModal";
import type { Cycle } from "@src/packages/cycles/hooks/useCycles";
import {
  cycleHadTriggerShot,
  getHubStepsForCycle,
} from "@louise/care/education";
import type { PgtPlan } from "@src/packages/cycles/presenters/resolveConditionalCycleGraph";
import { patientStepsAreComplete } from "@src/packages/cycles/presenters/stepProgressStatus";
import {
  resolveStepCopyPerspective,
  shouldUsePartnerCyclePerspective,
} from "@src/packages/cycles/partnerCycleContext";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";
import {
  ActionBanner,
  CardSurface,
  SectionTitle,
  SectionTitleRow,
  CompactActionButton,
  CompactIconTabs,
  KebabMenu,
  type KebabMenuItem,
  useToast,
} from "@louise/moon-ui";
import StatusBadge from "../../../../components/StatusBadge";
import { useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import { MoonHubCompletionView } from "./MoonHubCompletionView";
import { MoonScheduledStepView } from "./MoonScheduledStepView";
import { DateTimeFieldPicker } from "./DateTimeFieldPicker";
import { MoonStepFilledView } from "./MoonStepFilledView";
import {
  getMoonHubStepIcon,
  MoonHubGuidanceCarousel,
  type CycleContentContext,
} from "./MoonHubGuidanceCarousel";
import MedRow from "../../rows/MedRow";
import { DocumentRow } from "../../../documents/DocumentRow";
import { getProcedureSchedulingEntry } from "./procedureScheduling";
import { getStepIllustration } from "./stepIllustration";
import { StepDetailsModal } from "./StepDetailsModal";
import { IuiSpecimenLinkage } from "./IuiSpecimenLinkage";
import { FertilizationSpecimenPicker } from "./FertilizationSpecimenPicker";
import {
  useSpecimens,
  useRecordSpecimenMaterialOperation,
  createIdempotencyKey,
} from "@src/packages/care/cryovault/useSpecimens";
import { logClinicalMutationError } from "@src/packages/care/clinical-log-redaction";
import { getSingleCycleSelectedSpecimen } from "@src/packages/care/cryovault/frozenSpecimenAvailability";
import {
  deriveRecurrence,
  type MedicationScheduleItem,
} from "../../schedule.types";
import {
  useMedicationLogs,
  type MedicationLog,
} from "@src/packages/care/hooks/useMedicationLogs";
import { getTodayDoseStatus } from "@src/packages/care/medications";
import { useTimezone } from "@src/packages/preferences";
import { getTodayString } from "@src/packages/utils/date";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// =============================================================================
// Types
// =============================================================================

export interface HubCycle {
  id: string;
  cycleType: string;
  startDate: string | null;
  clinic: string | null;
  stepProgress: TreatmentStepProgress[] | null;
  currentPhase?: TreatmentPhaseName | null;
  phaseStartDate?: string | null;
  pgtPlan?: PgtPlan | null;
  contextSnapshot?: Cycle["contextSnapshot"];
  cyclePlanSnapshot?: Cycle["cyclePlanSnapshot"];
}

export interface MoonTreatmentHubCardProps {
  activeCycle: HubCycle | null;
  isCycleLoading: boolean;
  displayTreatmentName: string;
  /** Partner cycle projections are status-only until task ownership is explicit. */
  isReadOnly?: boolean;
  onEditTreatment: () => void;
  onUpdateStepProgress: (
    stepId: string,
    data: Partial<TreatmentStepProgress>,
  ) => Promise<void> | void;
  onOpenMedicationModal?: (opts: {
    cycleId: string;
    phaseId?: string | null;
    medicationRole?: "standard" | "trigger_shot" | null;
  }) => void;
  /**
   * Fired from the completion view's primary CTA once every step is done.
   * For pregnancy cycles this opens the two-week-wait surface; for
   * preservation cycles it triggers cycle-completion flow (Cryo Vault /
   * specimen modal). Naming intentionally avoids "WaitingPhase" because
   * preservation cycles don't enter one.
   */
  onFinishTreatment?: () => void;
  onStartTreatment?: () => void;
  goal: "pregnancy" | "preserve";
  /** Pregnancy cycles can enter the wait only after a confirmed procedure. */
  canEnterWaiting?: boolean;
  /**
   * Whether finishing the steps may be celebrated.
   *
   * Tone is decided by the ending, never by the surface — the same rule
   * `RecordedResultView` follows. Every step being ticked off says nothing about
   * how the cycle went, so a host that knows the cycle ended badly withholds
   * this and the banner does not appear. Defaults true: with no ending recorded
   * there is nothing to be insensitive about.
   */
  celebrateCompletion?: boolean;
  /**
   * Whether the treatment itself is behind her, however much of it she logged.
   *
   * The celebration used to require every step marked complete or skipped, which
   * almost no real cycle satisfies — a patient who never logged luteal support
   * leaves that step pending forever and never sees it. Reaching the end of
   * treatment is the thing worth marking, and it is not the same as having
   * ticked every box.
   */
  treatmentIsDone?: boolean;
  hasSpecimens?: boolean;
  cycleNumber?: number;
  onEditMedication?: (med: MedicationScheduleItem) => void;
  /** Open the existing CryoVault specimen-entry flow for a new semen sample. */
  onAddSpecimen?: () => void;
  /**
   * The host's toast for a vault write that failed after the step saved, with
   * its Open Cryo Vault action. The host owns the navigation.
   */
  onCryoVaultFollowUp?: (variant: "error" | "info", message: string) => void;
  /**
   * Open the app's appointment flow pre-filled to schedule a procedure step
   * (egg retrieval, transfer, IUI, sperm collection). The saved date is
   * mirrored back onto the step's phase by the host so the step becomes
   * "scheduled".
   */
  onScheduleProcedure?: (stepId: string) => void;
  /** Open the canonical specimen surface for an egg-retrieval outcome. */
  onRecordProcedureOutcome?: (stepId: string) => void;
  onAddResults?: (stepId: string) => void;
  /**
   * When true and the cycle is IVF/ICSI, the egg_retrieval step is hidden
   * because the patient is using previously frozen eggs.
   */
  usingFrozenSpecimens?: boolean;
  stepTimingById?: Record<string, string | null | undefined>;
  /**
   * Renders the cycle calendar as the hub's second ("Calendar") view. When
   * omitted, the Steps/Calendar toggle is hidden and only the journey shows.
   */
  renderCalendar?: () => ReactNode;
  /** Quick-add actions surfaced from the hub header "+" menu. */
  onQuickAddMedication?: () => void;
  onQuickAddAppointment?: () => void;
  onQuickAddTest?: () => void;
  /**
   * Clinically-derived per-step status keyed by step id ("completed" |
   * "active" | "upcoming"), computed by the host from the real phase dates.
   * When provided, the hub's "you are here" pointer follows it instead of the
   * patient's logged-step completion.
   */
  clinicalStepStatusById?: Record<string, "completed" | "active" | "upcoming">;
  /**
   * Lets the host replace a step's panel content entirely (e.g. the two-week
   * wait rendered as the terminal "Outcome" tab). Return content for the step
   * to override, or nothing to fall back to the default panel.
   */
  renderStepPanelOverride?: (stepId: string) => ReactNode;
  /**
   * Lets the host add content to a step's panel without replacing it — for data
   * the hub has no way to reach, like the beta values a clinic filed against
   * this cycle's order. It renders under the step's own actions and above the
   * guidance, because it is about her cycle and the guidance is not.
   */
  renderStepPanelExtra?: (stepId: string) => ReactNode;
  /**
   * Steps whose record belongs to the clinic rather than to her.
   *
   * Their panel shows the values and says where they came from, with no Edit —
   * a correction there cannot reach the clinic's record, and the honest place to
   * say so is before she types it, not after.
   */
  clinicRecordedStepIds?: readonly string[];
  /**
   * Labels for embryos picked from the CryoVault for an FET transfer. When
   * provided (and the cycle is FET), the hub shows them in a banner above the
   * transfer step so the patient sees which vault embryos this transfer is
   * for. Empty or absent means no banner.
   */
  frozenEmbryoLabelsForTransfer?: readonly string[];
  /**
   * The step this arrival is for, when the caller navigated here for one.
   *
   * Absent — the ordinary case — the hub opens on wherever the cycle has got to.
   * A step this cycle does not have is ignored rather than blanking the panel.
   */
  requestedStepId?: string | null;
  /**
   * Bumped by the host on every arrival. `requestedStepId` cannot say it alone:
   * the request outlives the navigation param, so arriving twice for the same
   * step leaves the id untouched and the panel would not follow the second time.
   */
  requestedStepArrival?: number;
}

const TEXT = semanticColors.text.primary;
const TEXT_MID = semanticColors.text.secondary;
const TEXT_LIGHT = semanticColors.text.tertiary;
const BORDER = semanticColors.border.subtle;
// Roadmap-tab fills: the active segment is bright enough to read as
// continuous with the step panel card it sits on; inactive segments are
// recessed so the strip behaves like MoonTabs folder tabs.
const SELECTED_BG = "rgba(255,255,255,0.66)";
const DEFAULT_STEP_BG = semanticColors.surface.subtle;
const LOCKED_STEP_BG = "rgba(255,255,255,0.24)";

const medicationEmptyIcon =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;

const scheduleAppointmentIcon =
  require("@src/apps/moon/assets/images/you-icons/notification-appointment-row-3d.png") as number;

const stepDetailsIcon =
  require("@src/apps/moon/assets/images/you-icons/notification-documents-row-3d.png") as number;

// Interim details-row copy per step (opens StepDetailsModal). Falls back to a
// generic "Add details" label for any future detailsModal step.
const STEP_DETAILS_TITLE_KEY: Record<string, string> = {
  screening: "care.treatment_hub.step_details.screening_title",
  pgt_testing: "care.treatment_hub.step_details.pgt_testing_title",
  fertilization: "care.treatment_hub.step_details.fertilization_title",
  embryo_development:
    "care.treatment_hub.step_details.embryo_development_title",
  freezing: "care.treatment_hub.step_details.freezing_title",
  analysis: "care.treatment_hub.step_details.enter_the_values_by_hand",
};

// The line under each details row. Most steps say the same generic thing.
// The analysis step has no entry: its details affordance is a pill, which
// carries no subtitle — the dialogue behind it names the figures.
const STEP_DETAILS_SUBTITLE_KEY: Record<string, string> = {
  screening: "care.treatment_hub.step_details.screening_upload_subtitle",
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * Imperative handle for the treatment hub. Lets a host that saves a scheduled
 * procedure through a sibling surface (e.g. the AddAppointmentModal in
 * CycleDetailScreen) push the "scheduled" status into the hub's optimistic
 * state before the phase refetch lands — otherwise the hub would keep showing
 * the "Planifier …" schedule row for the round-trip window.
 */
export interface MoonTreatmentHubCardHandle {
  applyScheduledOptimistic: (
    stepId: string,
    data?: Record<string, string | number | boolean | null>,
  ) => void;
  /**
   * Move the panel to a step because a sibling surface finished the work that
   * belongs to it — recording the retrieval's results in Cryo Vault leaves her
   * on the retrieval tab, while what she just entered is displayed on the
   * outcome tab. Counts as her having taken the wheel, so the journey pointer
   * does not walk the panel back off it a frame later.
   */
  selectStep: (stepId: string) => void;
}

export const MoonTreatmentHubCard = forwardRef<
  MoonTreatmentHubCardHandle,
  MoonTreatmentHubCardProps
>(function MoonTreatmentHubCard(
  {
    activeCycle,
    isCycleLoading,
    displayTreatmentName,
    isReadOnly = false,
    onUpdateStepProgress,
    onOpenMedicationModal,
    onFinishTreatment,
    onStartTreatment,
    goal,
    canEnterWaiting = true,
    celebrateCompletion = true,
    treatmentIsDone = false,
    hasSpecimens = false,
    cycleNumber = 1,
    onEditMedication,
    onAddSpecimen,
    onCryoVaultFollowUp,
    onScheduleProcedure,
    onRecordProcedureOutcome,
    onAddResults,
    usingFrozenSpecimens = false,
    stepTimingById,
    renderCalendar,
    onQuickAddMedication,
    onQuickAddAppointment,
    onQuickAddTest,
    clinicalStepStatusById,
    renderStepPanelOverride,
    renderStepPanelExtra,
    requestedStepId = null,
    requestedStepArrival = 0,
    clinicRecordedStepIds,
    frozenEmbryoLabelsForTransfer,
  }: MoonTreatmentHubCardProps,
  ref,
) {
  const t = useTranslations();
  const { showToast } = useToast();
  const { locale } = useLocale();
  const { client, tenantId, patientId } = useApiClient();
  const timezone = useTimezone();
  const today = getTodayString(timezone);

  const { data: phases = [] } = usePhases(activeCycle?.id);
  const { mutateAsync: createPhase } = useCreatePhase();
  const { data: patient } = usePatient();

  const isPartnerPerspective =
    isReadOnly &&
    shouldUsePartnerCyclePerspective({
      patient,
      cycleType: activeCycle?.cycleType,
    });
  // Copy perspective is decoupled from write-access: an AMAB primary editing
  // his own shared cycle keeps edit affordances (isPartnerPerspective stays
  // false) but reads partner-copy anatomy strings.
  const perspective: StepPerspective = resolveStepCopyPerspective({
    patient,
    cycleType: activeCycle?.cycleType,
  });
  const { data: partnerPatient } = usePatient({
    patientId: isPartnerPerspective ? patient?.partnerId : null,
  });
  const partnerDisplayName =
    perspective === "partner"
      ? partnerPatient?.firstName || partnerPatient?.preferredName || undefined
      : undefined;
  const isPartnerSourced = useCallback(
    (_stepId: string) => isPartnerPerspective,
    [isPartnerPerspective],
  );
  const effectiveOnEditMedication = isPartnerPerspective
    ? undefined
    : onEditMedication;
  const [locallyLinkedIuiSpecimenId, setLocallyLinkedIuiSpecimenId] = useState<
    string | null
  >(null);
  const persistedIuiSpecimenId = useMemo(() => {
    for (const stepId of ["insemination", "sperm_collection"]) {
      const progress = activeCycle?.stepProgress?.find(
        (entry) => entry.stepId === stepId,
      );
      const specimenId = progress?.data?.semenSpecimenId;
      if (typeof specimenId === "string" && specimenId.length > 0) {
        return specimenId;
      }
    }
    return null;
  }, [activeCycle?.stepProgress]);
  const hasLinkedIuiSpecimen = Boolean(
    persistedIuiSpecimenId ?? locallyLinkedIuiSpecimenId,
  );

  // Recording a `use` lifecycle event on the linked semen specimen when the
  // fertilization step is saved — sperm gets "used" (fertilize is what happens
  // to the oocyte). Without this, the vault keeps the sample showing as still
  // stored after the patient reports it was fertilized.
  const recordSpecimenMaterialOperation = useRecordSpecimenMaterialOperation();

  // Frozen semen selected for this cycle. The cycle snapshot records the
  // selection; older cycles without a snapshot can fall back to one
  // unambiguous reservation event. `specimen.cycleId` remains the origin
  // cycle, so it cannot identify material selected for this treatment.
  const needsFertilizationSemen =
    activeCycle?.cycleType === "ivf" || activeCycle?.cycleType === "icsi";
  const { data: semenSpecimens } = useSpecimens("semen", {
    enabled: needsFertilizationSemen,
  });
  const savedCycleSemenSpecimenId = activeCycle?.stepProgress?.find(
    (entry) => entry.stepId === "fertilization",
  )?.data?.semenSpecimenId;
  const cycleFrozenSemenSpecimen = useMemo(() => {
    if (!activeCycle?.id) return null;
    return getSingleCycleSelectedSpecimen(
      semenSpecimens ?? [],
      activeCycle.id,
      activeCycle.contextSnapshot?.materialIds,
      typeof savedCycleSemenSpecimenId === "string"
        ? savedCycleSemenSpecimenId
        : null,
    );
  }, [
    activeCycle?.contextSnapshot?.materialIds,
    activeCycle?.id,
    savedCycleSemenSpecimenId,
    semenSpecimens,
  ]);

  const steps = useMemo<TreatmentStepConfig[]>(
    () =>
      // Shared with Home, which explains the step she is on from the same
      // list. When frozen oocytes drive the cycle, the rewriter drops
      // `egg_retrieval`, retitles `protocol` as "Endometrial Preparation", and
      // renames `fertilization` to "Thaw & Fertilize".
      getHubStepsForCycle({
        cycleType: (activeCycle?.cycleType ?? "ivf") as CycleType,
        perspective,
        t,
        phases,
        pgtPlan: activeCycle?.pgtPlan,
        cyclePlanSnapshot: activeCycle?.cyclePlanSnapshot,
        hasLinkedIuiSpecimen,
        usingFrozenOocytes: usingFrozenSpecimens,
      }),
    [
      activeCycle?.cycleType,
      activeCycle?.pgtPlan,
      activeCycle?.cyclePlanSnapshot,
      perspective,
      phases,
      hasLinkedIuiSpecimen,
      usingFrozenSpecimens,
      t,
    ],
  );

  /** See `cycleHadTriggerShot`; Home reads it the same way. */
  const hadTriggerShot = useMemo(
    () => cycleHadTriggerShot(steps, phases),
    [steps, phases],
  );

  const baseStepProgress = useMemo(() => {
    if (!activeCycle?.stepProgress) return [] as TreatmentStepProgress[];
    if (activeCycle.cycleType === "egg_freezing") {
      return migrateEggFreezingStepProgress(activeCycle.stepProgress);
    }
    return activeCycle.stepProgress;
  }, [activeCycle?.stepProgress, activeCycle?.cycleType]);

  const [optimisticStepProgress, setOptimisticStepProgress] = useState<
    Record<string, TreatmentStepProgress>
  >({});
  /**
   * The step she is correcting, rather than recording for the first time.
   *
   * Editing works by committing the step back to `pending`, so by the time it is
   * confirmed again nothing distinguishes a correction from a first entry — and
   * completing a step advances the panel to the next pending one. That is right
   * on the way through the journey and wrong here: she asked to fix a value, so
   * the answer is that step's recap showing the fixed value, not the next step.
   */
  const [correctedStepId, setCorrectedStepId] = useState<string | null>(null);

  useEffect(() => {
    setOptimisticStepProgress({});
    setCorrectedStepId(null);
    setLocallyLinkedIuiSpecimenId(null);
    hasChosenStepRef.current = false;
  }, [activeCycle?.id]);

  const effectiveStepProgress = useMemo(() => {
    const byStepId = new Map<string, TreatmentStepProgress>();
    for (const progress of baseStepProgress) {
      byStepId.set(progress.stepId, progress);
    }
    for (const progress of Object.values(optimisticStepProgress)) {
      byStepId.set(progress.stepId, progress);
    }
    return Array.from(byStepId.values());
  }, [baseStepProgress, optimisticStepProgress]);

  const linkedIuiSpecimenId = useMemo(() => {
    for (const stepId of ["insemination", "sperm_collection"]) {
      const progress = effectiveStepProgress.find(
        (entry) => entry.stepId === stepId,
      );
      const specimenId = progress?.data?.semenSpecimenId;
      if (typeof specimenId === "string" && specimenId.length > 0) {
        return specimenId;
      }
    }
    return null;
  }, [effectiveStepProgress]);

  const isComplete = useMemo(() => {
    if (!steps.length || !effectiveStepProgress) return false;
    return patientStepsAreComplete(steps, (stepId) =>
      effectiveStepProgress.find((s) => s.stepId === stepId),
    );
  }, [effectiveStepProgress, steps]);

  /*
   * `endsInAResult` used to live here, to decide whether finishing the steps was
   * the end of the road or a milestone inside the cycle. It only ever gated the
   * completion panel's takeover of the strip, and that takeover is gone: the
   * strip stays for every flow, and the celebration is the banner. Removed
   * rather than left unread.
   */

  const currentStepId = useMemo(() => {
    if (!steps.length) return null;

    const progressFor = (stepId: string) =>
      effectiveStepProgress?.find((s) => s.stepId === stepId);

    // Prefer the clinically-derived per-step status when the host provides it
    // (it reads the real phase dates, so it knows the cycle has reached e.g.
    // stimulation even when the patient never logged the earlier steps). Sit on
    // the furthest "active" step; if none is active (between phases) ask
    // `resolveJourneyPointer`, which never falls behind the furthest step she
    // has resolved. It deliberately does *not* return the first unfinished step
    // any more: with retrieval and freezing resolved and stimulation never
    // filled, that rule pointed back at stimulation and the hub jumped
    // backwards under her.
    if (clinicalStepStatusById) {
      let activeId: string | null = null;
      for (const step of steps) {
        if (clinicalStepStatusById[step.id] === "active") activeId = step.id;
      }
      if (activeId) return activeId;
      /*
       * No active step — between phases. The pointer must not fall behind the
       * furthest step that is resolved, and that rule already exists as a
       * presenter, so this uses it rather than keeping a second copy that
       * disagrees. Scanning from the top for "not completed" was the same
       * backwards walk `resolveJourneyPointer` was written to replace, and it
       * survived here: on an egg-freezing cycle whose retrieval and freezing
       * were both done but whose stimulation was never filled, the hub opened
       * on `pre_treatment`.
       *
       * Fed her own answers, not the clinic's derived status, so this agrees
       * exactly with the pointer `CycleDetailScreen` gives the roadmap. Counting
       * a clinically-completed step as resolved would read as more correct and
       * would put the hub a step ahead of the roadmap: the map would mark one
       * step as where she is while the panel opened on another. A step the
       * clinic has dated but she has not answered is still a step she has not
       * answered.
       */
      return resolveJourneyPointer(steps, effectiveStepProgress);
    }

    // Fallback (no clinical status): infer from logged step progress. A step has
    // visibly begun when its phase has started (logging a medication or data
    // creates that phase, so the trigger shot lands us on trigger even if
    // earlier steps are blank) or it's already done.
    const hasBegun = (stepId: string): boolean => {
      const sp = progressFor(stepId);
      if (sp?.status === "completed" || sp?.status === "skipped") return true;
      if (hasMeaningfulStepData(sp?.data ?? undefined)) return true;
      if (sp?.phaseId) {
        const phase = phases.find((p) => p.id === sp.phaseId);
        if (phase?.startDate && phase.startDate <= today) return true;
      }
      return false;
    };

    // No clinical status at all — a host that does not derive one. This walk
    // reproduces `resolveJourneyPointer`'s rule by hand because it also needs
    // phase dates, which the presenter deliberately does not read. Unreachable
    // from `CycleDetailScreen`, which always supplies the status; kept for hosts
    // that do not, and worth folding into the presenter if a second one appears.
    //
    // Furthest step that has begun; the pointer never sits behind it.
    let lastBegun = -1;
    for (let i = 0; i < steps.length; i++) {
      if (hasBegun(steps[i]!.id)) lastBegun = i;
    }

    // From there, "current" is the first step that isn't finished. If nothing
    // has begun yet (fresh cycle), start from the top.
    for (let i = Math.max(0, lastBegun); i < steps.length; i++) {
      const sp = progressFor(steps[i]!.id);
      if (sp?.status !== "completed" && sp?.status !== "skipped") {
        return steps[i]!.id;
      }
    }
    return null;
  }, [clinicalStepStatusById, effectiveStepProgress, steps, phases, today]);

  const cycleDay = useMemo(() => {
    if (!activeCycle?.startDate) return null;
    const start = new Date(activeCycle.startDate + "T00:00:00");
    if (isNaN(start.getTime())) return null;
    const now = new Date();
    return (
      Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  }, [activeCycle?.startDate]);

  const STEP_TO_PHASE: Record<string, TreatmentPhaseName> = useMemo(
    () => ({
      pre_treatment: "getting_started" as TreatmentPhaseName,
      stimulation: "stimulation" as TreatmentPhaseName,
      protocol: "stimulation" as TreatmentPhaseName,
      trigger_shot: "trigger" as TreatmentPhaseName,
      egg_retrieval: "retrieval" as TreatmentPhaseName,
      freezing: "retrieval" as TreatmentPhaseName,
      fertilization: "fertilization" as TreatmentPhaseName,
      embryo_development: "fertilization" as TreatmentPhaseName,
      transfer: "transfer" as TreatmentPhaseName,
      luteal_phase: "waiting" as TreatmentPhaseName,
      sperm_collection: "retrieval" as TreatmentPhaseName,
      insemination: "retrieval" as TreatmentPhaseName,
      // Sperm freezing flow
      screening: "baseline" as TreatmentPhaseName,
      analysis: "retrieval" as TreatmentPhaseName,
    }),
    [],
  );

  // Estimated day labels for upcoming steps
  const PHASE_DURATION_DAYS: Record<string, [number, number]> = useMemo(
    () => ({
      getting_started: [7, 28],
      stimulation: [8, 14],
      trigger: [1, 2],
      retrieval: [1, 1],
      fertilization: [5, 7],
      transfer: [1, 1],
      waiting: [10, 14],
    }),
    [],
  );

  const estimatedDayLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    if (!activeCycle?.startDate || !steps.length) return labels;

    const DAY_MS = 24 * 60 * 60 * 1000;
    const startMs = new Date(`${activeCycle.startDate}T00:00:00`).getTime();
    if (Number.isNaN(startMs)) return labels;
    const dayLabel = (ms: number) =>
      formatDate(ms, locale, { month: "short", day: "numeric" });

    let cumulativeLow = 0;
    let cumulativeHigh = 0;
    let lastPhaseName: string | null = null;

    for (const step of steps) {
      const phaseName = STEP_TO_PHASE[step.id] ?? "getting_started";
      // Only add duration when entering a new phase
      if (phaseName !== lastPhaseName) {
        const [lo, hi] = PHASE_DURATION_DAYS[phaseName] ?? [1, 3];
        cumulativeLow += lo;
        cumulativeHigh += hi;
        lastPhaseName = phaseName;
      }

      const sp = effectiveStepProgress?.find((s) => s.stepId === step.id);
      const isPending = !sp || sp.status === "pending";

      if (isPending && step.id !== currentStepId) {
        // Estimate the calendar window this step lands in (cycle start + the
        // cumulative phase durations). Real dates read better than "Day 12".
        const startLabel = dayLabel(startMs + cumulativeLow * DAY_MS);
        const endLabel = dayLabel(startMs + cumulativeHigh * DAY_MS);
        labels[step.id] =
          cumulativeLow === cumulativeHigh
            ? startLabel
            : `${startLabel} – ${endLabel}`;
      }
    }
    return labels;
  }, [
    activeCycle?.startDate,
    steps,
    effectiveStepProgress,
    currentStepId,
    STEP_TO_PHASE,
    PHASE_DURATION_DAYS,
    locale,
  ]);

  // Fetch medications from API
  const { data: apiMedications } = useQuery({
    queryKey: ["medications", tenantId, patientId, activeCycle?.id],
    queryFn: async (): Promise<LocalMedication[]> => {
      if (!activeCycle?.id) return [];
      try {
        const response = await client.GET(
          "/{tenantId}/patients/{patientId}/medications",
          {
            params: { path: { tenantId, patientId } },
          },
        );
        if (response.error) return [];
        const allMeds = response.data?.data ?? [];
        const cycleMeds = allMeds.filter(
          (med) => med.cycleId === activeCycle.id,
        );
        return cycleMeds.map((med): LocalMedication => ({
          id: med.id,
          name:
            med.displayName ??
            med.medication?.name ??
            med.rawMedicationName ??
            "Unknown medication",
          startDate: med.startDate,
          endDate: med.endDate ?? null,
          timeTaken: Array.isArray(med.timeTaken) ? med.timeTaken : [],
          dose: med.dose ?? med.medication?.dose ?? null,
          doseUnit: med.doseUnit ?? med.medication?.doseUnit ?? null,
          intakeType: med.route ?? null,
          // Her administration method, carried rather than recomputed. See
          // `LocalMedication.form`.
          form: med.form ?? null,
          notes: med.notes,
          createdAt: med.createdAt,
          cycleId: med.cycleId ?? null,
          treatmentStepId: med.phaseId ?? null,
          /**
           * What she said it was, before what the catalogue thinks it is.
           *
           * This used to be recomputed on every read — hCG molecule, or the
           * trigger bit on the medication type — which quietly discarded the
           * role the add flow had just written. Adding a trigger from the
           * Trigger tab stamps `trigger_shot` (`handleAddMedication` below), and
           * that stamp was then overruled at read time by a purely
           * pharmacological guess: a trigger the catalogue does not recognise
           * came back with no role at all and appeared on no tab.
           *
           * The catalogue stays as the fallback, which is what answers for every
           * row written before the role was recorded.
           */
          medicationRole:
            recordedMedicationRole(med.medicationRole) ??
            ((med.medication?.type != null &&
              (med.medication.type & 8) !== 0) ||
            med.medication?.moleculeType === "hcg"
              ? "trigger_shot"
              : null),
        }));
      } catch {
        return [];
      }
    },
    enabled: !!activeCycle?.id && !!tenantId && !!patientId,
    staleTime: 30000,
  });

  const stepMedications = useMemo<Record<string, LocalMedication[]>>(() => {
    const medsMap: Record<string, LocalMedication[]> = {};
    if (!activeCycle?.id) return medsMap;

    const triggerPhaseId = resolveTriggerStepPhaseId(effectiveStepProgress);

    for (const step of steps) {
      if (apiMedications) {
        const sp = effectiveStepProgress?.find((s) => s.stepId === step.id);
        medsMap[step.id] = selectStepMedications({
          stepId: step.id,
          stepPhaseId: sp?.phaseId,
          triggerPhaseId,
          medications: apiMedications,
        });
      } else {
        medsMap[step.id] = [];
      }
    }
    return medsMap;
  }, [activeCycle?.id, steps, apiMedications, effectiveStepProgress]);

  const [selectedStepId, setSelectedStepId] = useState<string | null>(
    requestedStepId ?? null,
  );
  // The hub shows the guided journey by default; the "Calendar" toggle flips
  // to the cycle calendar (same cycle, dates lens) when the host provides one.
  const [hubView, setHubView] = useState<"steps" | "calendar">("steps");

  /**
   * A caller that navigated here for one step gets that step, and keeps it: the
   * arrival is the whole reason she is on this screen. Everything else still
   * opens on wherever the cycle has got to. An id this cycle has no step for is
   * ignored downstream by `selectedStep`, which falls back on its own.
   *
   * It follows the cycle only until she takes the wheel. Re-selecting on every
   * `currentStepId` change made this the last writer after any deliberate
   * navigation, and the panel that had just been chosen was replaced a frame
   * later: confirming the transfer dates the procedure, which makes the wait
   * eligible, which marks the outcome step active, which walks `currentStepId`
   * onto it. She saw the luteal step flash and landed on Result — the opposite
   * of the step the confirm had picked for her.
   *
   * A new arrival still wins, so a caller navigating here for a step gets it
   * whatever she was looking at, and a cycle switch clears the marker above.
   */
  const hasChosenStepRef = useRef(false);
  const lastRequestedStepIdRef = useRef(requestedStepId);
  const lastArrivalRef = useRef(requestedStepArrival);
  const specimenUseRequestIdsRef = useRef(new Map<string, string>());

  useEffect(() => {
    const arrivedForAStep =
      requestedStepId !== lastRequestedStepIdRef.current ||
      requestedStepArrival !== lastArrivalRef.current;
    lastRequestedStepIdRef.current = requestedStepId;
    lastArrivalRef.current = requestedStepArrival;
    if (hasChosenStepRef.current && !arrivedForAStep) return;
    setSelectedStepId(requestedStepId ?? currentStepId);
  }, [activeCycle?.id, currentStepId, requestedStepId, requestedStepArrival]);

  /**
   * Move the panel because someone meant to, as opposed to because the cycle
   * moved underneath it. Marks the wheel as taken so the effect above stops
   * following the journey pointer for the rest of this cycle.
   */
  const chooseStep = useCallback((stepId: string | null) => {
    hasChosenStepRef.current = true;
    setSelectedStepId(stepId);
  }, []);

  const handleSelectStep = useCallback(
    (stepId: string) => chooseStep(stepId),
    [chooseStep],
  );

  const findNextPendingStep = useCallback(
    (afterStepId: string) => {
      const idx = steps.findIndex((s) => s.id === afterStepId);
      return steps.slice(idx + 1).find((s) => {
        const sp = effectiveStepProgress?.find((p) => p.stepId === s.id);
        return !sp || sp.status === "pending";
      });
    },
    [steps, effectiveStepProgress],
  );

  const upsertOptimisticStepProgress = useCallback(
    (
      stepId: string,
      status: TreatmentStepProgress["status"],
      data?: Record<string, string | number | boolean | null>,
    ) => {
      const existing = effectiveStepProgress.find((s) => s.stepId === stepId);
      // Derive scheduledFor from the buffered date so the ScheduledStepView
      // renders immediately (before the API refetch lands).
      let scheduledFor: string | null = existing?.scheduledFor ?? null;
      if (status === "scheduled" && data) {
        const raw =
          (data.procedureDate as string | undefined) ??
          (data.transferDate as string | undefined) ??
          null;
        if (typeof raw === "string" && raw.length >= 10) {
          scheduledFor = raw.slice(0, 10);
        }
      } else if (status !== "scheduled") {
        scheduledFor = null;
      }
      setOptimisticStepProgress((prev) => ({
        ...prev,
        [stepId]: {
          stepId,
          phaseId: existing?.phaseId ?? null,
          status,
          completedAt:
            status === "pending" || status === "scheduled"
              ? null
              : (existing?.completedAt ?? new Date().toISOString()),
          scheduledFor,
          medications: existing?.medications ?? [],
          // Merge over the existing data instead of replacing it. Callers
          // pass only the fields their action changed (a schedule write
          // sends transferDate + appointmentId; a complete sends the
          // form's buffered fields). Replacing would drop prefilled
          // values the step already carried. The FET prefill from the
          // CryoVault pick is the case that surfaced this.
          data: { ...(existing?.data ?? {}), ...(data ?? {}) },
          notes: existing?.notes ?? null,
        },
      }));
    },
    [effectiveStepProgress],
  );

  // Host handle: sibling flows that already own the write (e.g. the
  // AddAppointmentModal saving a scheduled procedure) push the "scheduled"
  // status in here so the hub reflects it immediately, without waiting on the
  // phase refetch that `handleUpdateStepProgress` triggers.
  useImperativeHandle(
    ref,
    () => ({
      applyScheduledOptimistic: (stepId, data) => {
        upsertOptimisticStepProgress(stepId, "scheduled", data);
      },
      selectStep: (stepId) => {
        chooseStep(stepId);
      },
    }),
    [upsertOptimisticStepProgress, chooseStep],
  );

  // Apply the optimistic status, persist it, and revert to the prior state if
  // the save fails — otherwise the UI would keep showing a step as
  // completed/skipped that never actually saved.
  const commitStepProgress = useCallback(
    async (
      stepId: string,
      status: TreatmentStepProgress["status"],
      progress: Partial<TreatmentStepProgress>,
      data?: Record<string, string | number | boolean | null>,
    ): Promise<boolean> => {
      // Snapshot the existing optimistic entry (may be undefined) so we can
      // restore it verbatim on failure.
      let previous: TreatmentStepProgress | undefined;
      setOptimisticStepProgress((prev) => {
        previous = prev[stepId];
        return prev;
      });
      upsertOptimisticStepProgress(stepId, status, data);
      try {
        await onUpdateStepProgress(stepId, progress);
        return true;
      } catch {
        setOptimisticStepProgress((prev) => {
          const next = { ...prev };
          if (previous === undefined) {
            delete next[stepId];
          } else {
            next[stepId] = previous;
          }
          return next;
        });
        // Bring the user back to the step that failed (complete/skip advance
        // the selection on the way in) so the toast matches the visible step.
        chooseStep(stepId);
        showToast({
          variant: "error",
          message: t("care.treatment_hub.save_failed_message"),
        });
        return false;
      }
    },
    [
      chooseStep,
      onUpdateStepProgress,
      upsertOptimisticStepProgress,
      showToast,
      t,
    ],
  );

  const handleCompleteStep = useCallback(
    (
      stepId: string,
      data?: Record<string, string | number | boolean | null>,
    ) => {
      // Scheduling intent: the user picked a future procedureDate / transferDate.
      // CycleDetailScreen writes endDate=null in that case so deriveStepProgress
      // returns "scheduled" — the step is still the user's current focus, so we
      // must NOT auto-advance the panel away from it AND must commit the
      // optimistic status as "scheduled" so the auto-sync effect on
      // currentStepId doesn't jump to the next step.
      const todayIso = getTodayString();
      // The same three fields `TreatmentProgressModal` weighs. Left at two, a
      // sperm collection dated for next week resolved to "completed" here — the
      // card went green for something that had not happened, until the server
      // refetch corrected it.
      const isScheduling = (
        ["procedureDate", "transferDate", "collectionDate"] as const
      ).some((field) => {
        const raw = data?.[field];
        if (typeof raw !== "string" || raw.length < 10) return false;
        return raw.slice(0, 10) > todayIso;
      });
      // The server-side save handler (CycleDetailScreen) only branches on
      // "completed" / "skipped" / "pending" — it detects the future-date
      // case itself and writes endDate=null. So we ALWAYS send "completed"
      // to it. The optimistic client-side status is what drives the panel
      // UI (scheduled vs completed), so we commit that separately.
      const optimisticStatus: TreatmentStepProgress["status"] = isScheduling
        ? "scheduled"
        : "completed";
      // Read before the commit below overwrites it: whether a count was already
      // on record is what separates finishing the procedure from editing it.
      // Require the step itself to be completed. A value coming from the
      // CryoVault prefill leaves status pending, and treating that as
      // "recorded" would trap the patient on the transfer step on her first
      // save.
      const priorStepProgress = effectiveStepProgress.find(
        (entry) => entry.stepId === stepId,
      );
      const countAlreadyRecorded =
        priorStepProgress?.status === "completed" &&
        isUsableCount(priorStepProgress?.data?.embryosTransferred);
      const commitPromise = commitStepProgress(
        stepId,
        optimisticStatus,
        {
          status: "completed",
          completedAt: new Date().toISOString(),
          ...(data && Object.keys(data).length > 0 ? { data } : {}),
        },
        data,
      );
      if (correctedStepId === stepId) setCorrectedStepId(null);

      // Record the frozen sperm as used after the fertilization save
      // succeeds. A thaw is a separate lab event and does not mean the vial
      // was used; this step only records use when the form names a frozen
      // sample.
      // Idempotent per (specimen, cycle) so re-saving does not double-write.
      // Only fires if (a) the step save itself succeeded and (b) the submitted
      // form still names a frozen vial — a switch to "fresh" must not thaw.
      // The vial is the one she picked, which is the cycle's own sample by
      // default but can be any vial in her vault.
      const usedSemenSpecimenId =
        stepId === "fertilization" &&
        data?.spermType === "frozen" &&
        typeof data.semenSpecimenId === "string"
          ? data.semenSpecimenId
          : null;
      const usedSemenSpecimen = usedSemenSpecimenId
        ? (semenSpecimens?.find(
            (specimen) => specimen.id === usedSemenSpecimenId,
          ) ?? null)
        : null;
      if (!isScheduling && activeCycle?.id && usedSemenSpecimen) {
        const specimen = usedSemenSpecimen;
        const cycleId = activeCycle.id;
        const alreadyUsed =
          specimen.status === "used" ||
          (specimen.specimenUsages ?? []).some(
            (usage) => usage.cycleId === cycleId && usage.usage === "use",
          );
        if (!alreadyUsed) {
          void commitPromise.then((ok) => {
            if (!ok) return;
            const procedureDate =
              typeof data?.procedureDate === "string"
                ? data.procedureDate
                : undefined;
            const occurredAtDate = procedureDate?.slice(0, 10);
            const occurredAtTime = procedureDate?.includes("T")
              ? procedureDate.slice(11, 16)
              : "12:00";
            const requestKey = `${cycleId}:${specimen.id}`;
            const clientRequestId =
              specimenUseRequestIdsRef.current.get(requestKey) ??
              createIdempotencyKey();
            specimenUseRequestIdsRef.current.set(requestKey, clientRequestId);
            return recordSpecimenMaterialOperation
              .mutateAsync({
                id: specimen.id,
                operation: {
                  clientRequestId,
                  action: "use",
                  occurredAt: occurredAtDate
                    ? `${occurredAtDate}T${occurredAtTime}:00.000Z`
                    : new Date().toISOString(),
                  quantity: "1",
                  unit: "vial",
                  cycleId,
                  hasDecisionAndConsent: true,
                },
              })
              .catch((error: unknown) => {
                logClinicalMutationError(
                  "[MoonTreatmentHubCard]",
                  `POST /specimens/${specimen.id}/material-operations`,
                  error,
                );
                // The step itself is saved. Say so, and say where the use can
                // still be recorded, rather than leave the vault silently
                // showing the sample as stored. The host's toast carries the
                // Open Cryo Vault action.
                const message = t(
                  "care.treatment_hub.record_frozen_sample_use_in_cryovault",
                );
                if (onCryoVaultFollowUp) {
                  onCryoVaultFollowUp("error", message);
                } else {
                  showToast({ variant: "error", message, duration: 10000 });
                }
              });
          });
        }
      }

      // The transfer step advances on one fact only: the number of embryos
      // transferred. Everything else about a transfer — its date, its time, a
      // correction to either — leaves her where she is, because she has not
      // finished telling us what happened. The count is the last piece, so it is
      // the one that moves her on.
      //
      // And on to the adjacent step, not `findNextPendingStep`. That returns the
      // first *unfinished* step, which steps over a luteal phase already recorded
      // or skipped and landed her two tabs away on the outcome.
      if (stepId === TRANSFER_STEP_ID) {
        // The *first* count is what finishes the procedure. Confirming a count
        // that was already on record is editing it, and editing keeps her where
        // she is — moving her on would treat a correction as if she had just
        // completed the transfer.
        const confirmsFirstCount =
          isUsableCount(data?.embryosTransferred) && !countAlreadyRecorded;
        if (isScheduling || !confirmsFirstCount) {
          chooseStep(stepId);
          return;
        }
        const index = steps.findIndex((step) => step.id === stepId);
        chooseStep((index >= 0 ? steps[index + 1]?.id : undefined) ?? stepId);
        return;
      }

      if (correctedStepId === stepId) {
        chooseStep(stepId);
        return;
      }
      if (!isScheduling) {
        const next = findNextPendingStep(stepId);
        chooseStep(next?.id ?? null);
      }
    },
    [
      chooseStep,
      commitStepProgress,
      correctedStepId,
      effectiveStepProgress,
      findNextPendingStep,
      steps,
      activeCycle?.id,
      semenSpecimens,
      recordSpecimenMaterialOperation,
      onCryoVaultFollowUp,
      showToast,
      t,
    ],
  );

  const handleSkipStep = useCallback(
    (stepId: string) => {
      // Only for the step it belongs to. Clearing it for any step meant editing
      // A, skipping C, then confirming A found no marker — and A's confirm went
      // back to buffering data instead of completing.
      setCorrectedStepId((current) => (current === stepId ? null : current));
      void commitStepProgress(stepId, "skipped", {
        status: "skipped",
        completedAt: new Date().toISOString(),
      });
      const next = findNextPendingStep(stepId);
      chooseStep(next?.id ?? null);
    },
    [chooseStep, commitStepProgress, findNextPendingStep],
  );

  const handleStartEditing = useCallback(
    (stepId: string) => {
      setCorrectedStepId(stepId);
      void commitStepProgress(stepId, "pending", {
        status: "pending",
        completedAt: null,
      });
      chooseStep(stepId);
    },
    [chooseStep, commitStepProgress],
  );

  const handleUndoStep = useCallback(
    (stepId: string) => {
      // Same commit as editing, so it needs the same marker: a step reverted to
      // pending is one she is standing on, and without this its confirm routes
      // to `onUpdateData` and does nothing at all.
      setCorrectedStepId(stepId);
      void commitStepProgress(stepId, "pending", {
        status: "pending",
        completedAt: null,
      });
    },
    [commitStepProgress],
  );

  const selectedStep = useMemo(() => {
    if (!steps.length) return null;
    /*
     * A null pointer means the journey has no step left to answer — she is at
     * the end of it, not back at the beginning. Falling to `steps[0]` opened a
     * finished egg-freezing cycle on `pre_treatment`.
     */
    const journeyEnd = steps[steps.length - 1] ?? null;
    const fallbackStep =
      steps.find((step) => step.id === currentStepId) ??
      (currentStepId === null ? journeyEnd : (steps[0] ?? null));
    if (!selectedStepId) return fallbackStep;
    return steps.find((step) => step.id === selectedStepId) ?? fallbackStep;
  }, [currentStepId, selectedStepId, steps]);

  const selectedStepDetails = useMemo(() => {
    if (!selectedStep) return null;
    const partnerSourced = isPartnerSourced(selectedStep.id);
    const sp =
      effectiveStepProgress.find((s) => s.stepId === selectedStep.id) ?? null;
    const medications = stepMedications[selectedStep.id] ?? [];
    // A step she is correcting is one she is standing on, whatever the journey
    // thinks comes next. Without this it reads as "upcoming" — `currentStepId`
    // prefers the clinically-derived status, which has already moved past a
    // recorded transfer — and an upcoming step's confirm only buffers data
    // (`onUpdateData`) instead of completing the step, so pressing Confirm after
    // an edit did nothing at all.
    const isCurrent =
      selectedStep.id === currentStepId || correctedStepId === selectedStep.id;
    const isDone = sp?.status === "completed" || sp?.status === "skipped";
    /**
     * A gap she has already moved past, not a plan.
     *
     * Badging this "Planned" beside an estimated date range announced as future
     * a step whose own result was already recorded. The rule lives with the
     * pointer that steps over these gaps, so the two cannot disagree about
     * which ones they are.
     */
    const holdsHerData =
      medications.length > 0 ||
      hasMeaningfulStepData(sp?.data) ||
      sp?.status === "scheduled";
    /**
     * A gap she has already moved past, not a plan — and genuinely empty.
     *
     * `isStepPassedUnfilled` answers "did the journey step over this one",
     * which is about position, not content. A step where she logged her
     * medications and never pressed Next has been stepped over too, and badging
     * it "not filled in" directly above the drugs she entered calls her a liar.
     * Content she has put there is an answer of its own, whatever its status.
     */
    const isPassedUnfilled =
      !holdsHerData &&
      isStepPassedUnfilled(selectedStep.id, steps, effectiveStepProgress);
    const hasPlannedData = !isDone && !isPassedUnfilled && holdsHerData;
    // Only the fertilization step reads defaults, from two places. Other
    // prefill sources can follow the same pattern (attach here, then
    // StepDetailsModal merges saved data on top).
    //
    // Her answers on this cycle's sperm_collection step come first, so she
    // never answers the same question twice; a last-minute switch on
    // fertilization itself still wins, because saved data goes on top. The
    // semen sample the cycle was created from fills in whatever
    // sperm_collection left unanswered.
    //
    // Kept out of the step's progress on purpose: a default is not her data,
    // and progress that looked filled would badge an untouched fertilization
    // step as planned.
    let dataPrefill:
      Record<string, string | number | boolean | null> | undefined;
    let dataPrefillBanner: string | null | undefined;
    if (selectedStep.id === "fertilization") {
      const prefill: Record<string, string | number | boolean | null> = {};
      if (cycleFrozenSemenSpecimen) {
        prefill.semenSpecimenId = cycleFrozenSemenSpecimen.id;
        prefill.spermType = "frozen";
        // The fertilization form's spermSource select only offers
        // partner/donor. Preserve a source only when specimen.origin maps to
        // one of those choices. "mine" and "unknown" do not tell us who
        // supplied sperm for this cycle, so leave the field unanswered rather
        // than guessing.
        const origin = cycleFrozenSemenSpecimen.origin;
        if (origin === "partner" || origin === "donor") {
          prefill.spermSource = origin;
        }
      }
      const collected = effectiveStepProgress.find(
        (candidate) => candidate.stepId === "sperm_collection",
      )?.data;
      if (typeof collected?.spermSource === "string") {
        prefill.spermSource = collected.spermSource;
      }
      if (typeof collected?.spermType === "string") {
        prefill.spermType = collected.spermType;
        // A fresh sample has no vial, whichever one the cycle started from.
        if (collected.spermType !== "frozen") delete prefill.semenSpecimenId;
      }
      if (
        collected?.spermType === "frozen" &&
        typeof collected.semenSpecimenId === "string"
      ) {
        prefill.semenSpecimenId = collected.semenSpecimenId;
      }
      if (Object.keys(prefill).length > 0) dataPrefill = prefill;
    }
    // The banner names the cycle's own sample, so it only shows while that
    // is the vial the form starts on.
    if (
      cycleFrozenSemenSpecimen &&
      dataPrefill?.semenSpecimenId === cycleFrozenSemenSpecimen.id
    ) {
      // Build the identifier the patient will recognise: a number is more
      // useful than a UUID `sampleId` clinics often set. Use the API order,
      // which is also the order CryoVault uses when numbering specimens.
      const isUuid = (value: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          value,
        );
      const readableSampleId =
        cycleFrozenSemenSpecimen.sampleId &&
        !isUuid(cycleFrozenSemenSpecimen.sampleId)
          ? cycleFrozenSemenSpecimen.sampleId
          : null;
      const index =
        (semenSpecimens ?? []).findIndex(
          (s) => s.id === cycleFrozenSemenSpecimen.id,
        ) + 1;
      const sampleLabel =
        readableSampleId ??
        (index > 0
          ? t("cryo_vault.labels.sample_label").replace("${n}", String(index))
          : null);
      const facility =
        cycleFrozenSemenSpecimen.storageLocations?.find(
          (loc) => loc.isActive && loc.storageType === "frozen",
        )?.facility ?? null;
      if (sampleLabel && facility) {
        dataPrefillBanner = t(
          "care.treatment_hub.step_details.using_frozen_sperm_from_vault_labeled_at_clinic",
          { params: { sample: sampleLabel, clinic: facility } },
        );
      } else if (sampleLabel) {
        dataPrefillBanner = t(
          "care.treatment_hub.step_details.using_frozen_sperm_from_vault_labeled",
          { params: { sample: sampleLabel } },
        );
      } else if (facility) {
        dataPrefillBanner = t(
          "care.treatment_hub.step_details.using_frozen_sperm_from_vault_at_clinic",
          { params: { clinic: facility } },
        );
      } else {
        dataPrefillBanner = t(
          "care.treatment_hub.step_details.using_frozen_sperm_from_vault_unlabeled",
        );
      }
    }
    return {
      step: selectedStep,
      stepProgress: sp,
      partnerSourced,
      clinicRecorded: clinicRecordedStepIds?.includes(selectedStep.id) ?? false,
      isCurrent,
      isCompleted: sp?.status === "completed",
      isSkipped: sp?.status === "skipped",
      isPassedUnfilled,
      isScheduled: sp?.status === "scheduled",
      scheduledFor: sp?.scheduledFor ?? null,
      /**
       * Upcoming suppresses the complete/skip actions on medication steps. A
       * passed-unfilled step is the opposite of upcoming — the badge tells her
       * it is hers to answer — so leaving it in this set asked for an answer
       * while hiding every way to give one, on a step that is not skippable
       * either. It could then never be closed.
       */
      isUpcoming: !isDone && !isCurrent && !isPassedUnfilled,
      hasPlannedData,
      medications,
      /**
       * A real recorded timing always shows. An *estimate* does not, once she
       * has moved past the step: "Estimated 11–13 June" beside a step whose own
       * result is already recorded is the same false promise as the "Planned"
       * badge next to it, and predicting a window she is already through reads
       * as the app having lost track of her.
       */
      timingLabel:
        stepTimingById?.[selectedStep.id] ??
        (isPassedUnfilled ? null : estimatedDayLabels[selectedStep.id]) ??
        null,
      /**
       * Whether the retrieval's result is already on the record.
       *
       * That is a fact about the cycle, so it is read off the specimens the
       * cycle holds. It used to be derived from the journey's last step, which
       * was wrong in both directions. For egg freezing the last step is
       * `freezing`, which shares the `egg_retrieval` API phase
       * (`STEP_TO_PHASE_TYPE`) and reads the same metrics through a mapping
       * that is not step-scoped — so filling the retrieval's own form flipped
       * this, and the row offered to "edit the result" before any result
       * existed, opening an empty form. For ivf/icsi/iui/fet/oi the last step
       * is the appended `outcome`, which carries `derivedProgress` and is
       * therefore permanently pending with no data, so it was structurally
       * always false.
       */
      outcomeResultsRecorded: hasSpecimens,
      isLastStep: steps[steps.length - 1]?.id === selectedStep.id,
      dataPrefill,
      dataPrefillBanner,
    };
  }, [
    selectedStep,
    steps,
    isPartnerSourced,
    clinicRecordedStepIds,
    effectiveStepProgress,
    currentStepId,
    correctedStepId,
    stepMedications,
    stepTimingById,
    estimatedDayLabels,
    hasSpecimens,
    cycleFrozenSemenSpecimen,
    semenSpecimens,
    t,
  ]);

  // Resolved once: the branch below both tests it and renders it.
  const stepPanelOverride = selectedStepDetails
    ? renderStepPanelOverride?.(selectedStepDetails.step.id)
    : undefined;

  const selectedStepNeedsMedicationLogs = Boolean(
    selectedStepDetails &&
    !selectedStepDetails.partnerSourced &&
    !selectedStepDetails.isCompleted &&
    !selectedStepDetails.isSkipped &&
    selectedStepDetails.step.contentType === "medications" &&
    selectedStepDetails.medications.length > 0,
  );

  const todayMedicationLogs = useMedicationLogs({
    startDate: today,
    endDate: today,
    enabled: selectedStepNeedsMedicationLogs,
  });

  const handleUpdateData = useCallback(
    (
      stepId: string,
      data: Record<string, string | number | boolean | null>,
    ) => {
      if (
        stepId === "insemination" &&
        typeof data.semenSpecimenId === "string"
      ) {
        setLocallyLinkedIuiSpecimenId(data.semenSpecimenId);
      }
      void commitStepProgress(stepId, "pending", { data }, data);
    },
    [commitStepProgress],
  );

  const ensurePhaseExists = useCallback(
    async (stepId: string): Promise<string | null> => {
      if (!activeCycle?.id) return null;
      const phaseType = getPhaseTypeForStep(stepId);
      if (!phaseType) return null;

      const sp = effectiveStepProgress?.find((s) => s.stepId === stepId);
      if (sp?.phaseId) return sp.phaseId;

      const existingPhase = phases.find((p) => p.type === phaseType);
      if (existingPhase) return existingPhase.id;

      try {
        const today = getTodayString();
        // pre_treatment by definition starts when the cycle starts.
        // Stamping `today` would make the phase look like Day 1 even when
        // the patient registered the cycle days or weeks after starting.
        // Other phases keep `today` as the default and get reconciled
        // against actual medication start dates by the parent screen.
        const defaultStartDate =
          stepId === "pre_treatment" && activeCycle.startDate
            ? activeCycle.startDate
            : today;
        const newPhase = await createPhase({
          cycleId: activeCycle.id,
          body: { type: phaseType, startDate: defaultStartDate },
        });
        return newPhase.id;
      } catch (err) {
        console.warn(
          "[MoonTreatmentHubCard] Failed to create phase for medication:",
          err,
        );
        return null;
      }
    },
    [
      activeCycle?.id,
      activeCycle?.startDate,
      effectiveStepProgress,
      phases,
      createPhase,
    ],
  );

  const handleAddMedication = useCallback(
    async (stepId: string) => {
      if (!activeCycle?.id || !onOpenMedicationModal) return;
      const phaseId = await ensurePhaseExists(stepId);
      /**
       * The trigger *step* is itself a medication step — anything logged there
       * is a trigger shot, dual triggers included — so tag the role from the
       * step's identity, not from `hasTriggerShot`.
       *
       * `hasTriggerShot` says the step's protocol *includes* a trigger, and on
       * ivf, icsi and embryo freezing it sits on `protocol`, the stimulation
       * tab. Reading it here stamped every Gonal-F and Cetrotide added there as
       * a trigger shot. Nothing surfaced it while the role was recomputed on
       * read, but the role is now trusted, so the writer has to be right.
       */
      onOpenMedicationModal({
        cycleId: activeCycle.id,
        phaseId,
        medicationRole: stepId === TRIGGER_STEP_ID ? "trigger_shot" : null,
      });
    },
    [activeCycle?.id, onOpenMedicationModal, ensurePhaseExists],
  );

  const handleComplete = useCallback(() => {
    onFinishTreatment?.();
  }, [onFinishTreatment]);

  /**
   * The same words the completion view puts on the same act, so the button on
   * the journey's last step is the button she already knows from the hub rather
   * than a second one that reads differently.
   */
  const closeCycleLabel = useMemo(() => {
    if (goal === "preserve") {
      return t("care.treatment_hub.completion.cta_complete_this_cycle");
    }
    return canEnterWaiting
      ? t("care.treatment_hub.completion.cta_enter_waiting")
      : t("care.treatment_hub.completion.cta_complete_cycle");
  }, [goal, canEnterWaiting, t]);

  const nextPendingStep = useMemo(() => {
    if (!steps.length) return null;
    return (
      steps.find((s) => {
        const sp = effectiveStepProgress.find((p) => p.stepId === s.id);
        return !sp || sp.status === "pending";
      }) ?? null
    );
  }, [steps, effectiveStepProgress]);

  // Empty state — "Start Your Treatment" CTA
  if (!activeCycle && !isCycleLoading) {
    return (
      <View style={{ marginTop: 24, marginHorizontal: -16 }}>
        <View style={{ marginBottom: 10, paddingHorizontal: 18 }}>
          <SectionTitle icon={Activity}>
            {t("care.treatment_hub.title")}
          </SectionTitle>
        </View>
        <CardSurface style={{ marginHorizontal: 16, padding: 24, gap: 16 }}>
          <View className="flex-row items-center gap-3">
            <Play size={22} color={EMBER.colors.textLight} strokeWidth={1.8} />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 16,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                  lineHeight: 20,
                }}
              >
                {t("care.treatment_hub.start_your_treatment")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textMid,
                  lineHeight: 18,
                }}
              >
                {t("care.treatment_hub.start_your_treatment_help")}
              </Text>
            </View>
          </View>

          {onStartTreatment && (
            <CompactActionButton
              label={t("care.treatment_hub.start_tracking")}
              onPress={onStartTreatment}
              trailingIcon={ChevronRight}
              variant="primary"
              minWidth={168}
            />
          )}
        </CardSurface>
      </View>
    );
  }

  const showCalendarToggle = Boolean(renderCalendar) && !isPartnerPerspective;
  const isCalendarView = showCalendarToggle && hubView === "calendar";

  const quickAddItems: KebabMenuItem[] = [];
  if (onQuickAddMedication && !isPartnerPerspective) {
    quickAddItems.push({
      id: "medication",
      label: t("care.treatment_hub.quick_add.medication"),
      icon: Pill,
      onPress: onQuickAddMedication,
    });
  }
  if (onQuickAddAppointment && !isPartnerPerspective) {
    quickAddItems.push({
      id: "appointment",
      label: t("care.treatment_hub.quick_add.appointment"),
      icon: CalendarPlus,
      onPress: onQuickAddAppointment,
    });
  }
  if (onQuickAddTest && !isPartnerPerspective) {
    quickAddItems.push({
      id: "test",
      label: t("care.treatment_hub.quick_add.test"),
      icon: FlaskConical,
      onPress: onQuickAddTest,
    });
  }

  const headerControls =
    showCalendarToggle || quickAddItems.length > 0 ? (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        {showCalendarToggle ? (
          <CompactIconTabs
            options={[
              {
                value: "steps",
                label: t("care.treatment_hub.view_steps"),
                icon: ListChecks,
                accessibilityLabel: t("care.treatment_hub.view_a11y", {
                  params: { label: t("care.treatment_hub.view_steps") },
                }),
              },
              {
                value: "calendar",
                label: t("care.treatment_hub.view_calendar"),
                icon: Calendar,
                accessibilityLabel: t("care.treatment_hub.view_a11y", {
                  params: { label: t("care.treatment_hub.view_calendar") },
                }),
              },
            ]}
            value={hubView}
            onChange={setHubView}
            compact
          />
        ) : null}
        {quickAddItems.length > 0 ? (
          <KebabMenu
            items={quickAddItems}
            triggerIcon={Plus}
            triggerIconSize={16}
            buttonColor={TEXT_LIGHT}
            accessibilityLabel={t("care.treatment_hub.quick_add.menu_a11y")}
            triggerStyle={{
              borderWidth: 1,
              borderColor: semanticColors.border.glass,
              backgroundColor: semanticColors.surface.subtle,
            }}
          />
        ) : null}
      </View>
    ) : undefined;

  return (
    <View style={{ marginTop: 24, marginHorizontal: -16 }}>
      <View style={{ marginBottom: 10, paddingHorizontal: 18 }}>
        <SectionTitleRow
          title={t("care.treatment_hub.title")}
          icon={Activity}
          right={headerControls}
        />
      </View>

      {/**
       * Finishing the steps no longer takes the hub away from her.
       *
       * A preservation cycle has no `outcome` step, so `!endsInAResult` held and
       * resolving the last pending step swapped the whole strip for the
       * completion panel. Pressing "Next" on the trigger step therefore threw
       * her out of her own tabs, with no way back to the steps she was still
       * filling — which read as the cycle having been closed, though nothing was
       * written. The strip stays; the celebration is the banner below, which is
       * already how every result-bearing flow marks the same moment.
       *
       * What the panel carried, and where it went. The end-of-cycle recap
       * belongs behind the act of closing, and the close dialog carries one —
       * `PreservationRecap`, the counts and the stored specimens, not a
       * per-step summary; that reading of it was wrong. The Cryo Vault is
       * reached from this screen's own Cryo Vault section rather than through
       * the hub. And the panel's trailing "Complete this cycle" button is now
       * on the journey's last step, where it is reachable without every
       * optional middle step being filled first.
       */}
      {isCalendarView ? (
        <View style={{ marginHorizontal: 16 }}>{renderCalendar?.()}</View>
      ) : (
        <View style={{ marginHorizontal: 16, gap: 0 }}>
          {/* Finishing the steps is worth marking, but it is not the end of the
              cycle for a flow that ends in a result — the wait and the result
              are still ahead, on their own tab. So the celebration sits above
              the strip instead of replacing it. */}
          {(isComplete || treatmentIsDone) && celebrateCompletion ? (
            <MoonHubCompletionView
              presentation="banner"
              steps={steps}
              stepProgress={effectiveStepProgress}
              stepMedications={stepMedications}
              treatmentName={displayTreatmentName}
              clinicName={activeCycle?.clinic ?? null}
              cycleDay={cycleDay}
              cycleNumber={cycleNumber}
              goal={goal}
              cycleType={activeCycle?.cycleType}
            />
          ) : null}

          <MoonHubBreadcrumbStepper
            steps={steps}
            stepProgress={effectiveStepProgress}
            currentStepId={currentStepId}
            selectedStepId={selectedStep?.id ?? currentStepId}
            onSelectStep={handleSelectStep}
          />

          {selectedStepDetails ? (
            <Animated.View
              key={selectedStepDetails.step.id}
              entering={FadeIn.duration(220)}
            >
              {/* The extra belongs to the step, not to the default panel. It
                  used to render inside `MoonHubSelectedStepPanel`, which an
                  override short-circuits — so on FET the clinic's beta series
                  vanished the moment a result was recorded and `recordedResultView`
                  took the tab, which is exactly when a second value is worth
                  reading. Rendered here it survives the override. */}
              {stepPanelOverride ? (
                <>
                  {stepPanelOverride}
                  {renderStepPanelExtra?.(selectedStepDetails.step.id) ?? null}
                </>
              ) : (
                <MoonHubSelectedStepPanel
                  cycleContext={{
                    cycleId: activeCycle?.id ?? null,
                    // Read as the step list above reads it.
                    cycleType: activeCycle
                      ? (activeCycle.cycleType as CycleType)
                      : null,
                    usingFrozenSpecimens,
                    cycleHadTriggerShot: hadTriggerShot,
                  }}
                  details={selectedStepDetails}
                  perspective={perspective}
                  partnerDisplayName={partnerDisplayName}
                  nextPendingStep={nextPendingStep}
                  frozenEmbryoLabelsForTransfer={frozenEmbryoLabelsForTransfer}
                  onSelectStep={handleSelectStep}
                  onCloseCycle={
                    isPartnerPerspective || !onFinishTreatment
                      ? undefined
                      : handleComplete
                  }
                  closeCycleLabel={closeCycleLabel}
                  renderPanelExtra={renderStepPanelExtra}
                  onComplete={handleCompleteStep}
                  onSkip={handleSkipStep}
                  onUndo={handleUndoStep}
                  onUpdateData={handleUpdateData}
                  linkedIuiSpecimenId={linkedIuiSpecimenId}
                  onAddSpecimen={
                    isPartnerPerspective ? undefined : onAddSpecimen
                  }
                  onAddMedication={handleAddMedication}
                  onStartEditing={handleStartEditing}
                  onScheduleProcedure={onScheduleProcedure}
                  onRecordProcedureOutcome={onRecordProcedureOutcome}
                  onAddResults={onAddResults}
                  onEditMedication={effectiveOnEditMedication}
                  todayMedicationLogs={todayMedicationLogs.data}
                  medicationLogDate={today}
                  medicationLogTimezone={timezone}
                />
              )}
            </Animated.View>
          ) : null}
        </View>
      )}
    </View>
  );
});

type SelectedStepDetails = {
  step: TreatmentStepConfig;
  stepProgress: TreatmentStepProgress | null;
  partnerSourced: boolean;
  clinicRecorded: boolean;
  isCurrent: boolean;
  isCompleted: boolean;
  isSkipped: boolean;
  /** A gap she has moved past without answering — see `isStepPassedUnfilled`. */
  isPassedUnfilled: boolean;
  isScheduled: boolean;
  scheduledFor: string | null;
  isUpcoming: boolean;
  hasPlannedData: boolean;
  medications: LocalMedication[];
  timingLabel: string | null;
  /**
   * Whether the journey's closing step already holds recorded data — read from
   * that step, not from the one being rendered.
   *
   * The retrieval panel needs it to stop offering "add retrieval results" over
   * results she has already entered, and to point her at the tab that holds
   * them.
   *
   * Derived from the last step of whatever flow this is rather than from a
   * hardcoded id, because the three preservation journeys do not agree on one:
   * egg and sperm freezing end on `freezing`, embryo freezing has no such step
   * and ends on embryo development or PGT.
   */
  outcomeResultsRecorded: boolean;
  /**
   * Whether this is the journey's last step. The cycle can be closed from there
   * at any point — closing is her decision, not a reward for a complete form.
   */
  isLastStep: boolean;
  /**
   * Field defaults sourced from the cycle itself (e.g. the frozen semen sample
   * this cycle was picked for). Saved data always overrides these; a
   * first-time visit opens with the values already filled.
   */
  dataPrefill?: Record<string, string | number | boolean | null>;
  /** Optional short line above the details form when data is being prefilled. */
  dataPrefillBanner?: string | null;
};

function MoonHubBreadcrumbStepper({
  steps,
  stepProgress,
  currentStepId,
  selectedStepId,
  onSelectStep,
}: {
  steps: TreatmentStepConfig[];
  stepProgress: TreatmentStepProgress[];
  currentStepId: string | null;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MoonHubBreadcrumbStepper");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-end",
        width: "100%",
        overflow: "visible",
        paddingHorizontal: 1,
      }}
    >
      {steps.map((step, index) => {
        const sp = stepProgress.find((s) => s.stepId === step.id);
        const isCompleted = sp?.status === "completed";
        const isSkipped = sp?.status === "skipped";
        const isCurrent = step.id === currentStepId;
        const isSelected = step.id === selectedStepId;
        // A step she has moved past is not locked. Dimming it as unreachable is
        // the same claim the panel one row below now retracts, and it is the
        // gap she is being invited to fill.
        const isLocked =
          !isCurrent &&
          !isCompleted &&
          !isSkipped &&
          !isStepPassedUnfilled(step.id, steps, stepProgress);
        const Icon = getMoonHubStepIcon(step);
        const label = getBreadcrumbStepLabel(t, step);
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;
        const bg = isSelected
          ? SELECTED_BG
          : isLocked
            ? LOCKED_STEP_BG
            : DEFAULT_STEP_BG;
        const borderColor = isSelected ? semanticColors.border.glass : BORDER;
        const textColor = isSelected || isCurrent ? TEXT : TEXT_LIGHT;
        const clipPath = getBreadcrumbClipPath(index, steps.length);
        const isHovered = hoveredId === step.id;

        return (
          <Pressable
            key={step.id}
            onPress={() => {
              // Step labels are translated flow copy, so pair with a stable action.
              capturePress(pressLabel(label), "hub_breadcrumb_step");
              onSelectStep(step.id);
            }}
            onHoverIn={() => setHoveredId(step.id)}
            onHoverOut={() =>
              setHoveredId((current) => (current === step.id ? null : current))
            }
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
            accessibilityLabel={step.shortTitle ?? step.title}
            style={({ pressed }) => ({
              flex: 1,
              minWidth: 0,
              // The active tab stands a touch taller and reaches down into the
              // panel card (paddingBottom + no bottom border) so it reads as
              // one connected surface — the MoonTabs folder-tab metaphor.
              height: isSelected ? 44 : 38,
              marginRight: isLast ? 0 : -5,
              paddingLeft: isFirst ? 8 : 14,
              paddingRight: isLast ? 8 : 13,
              paddingBottom: isSelected ? 6 : 0,
              borderTopLeftRadius: isFirst ? 14 : 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: isLast ? 14 : 0,
              borderBottomRightRadius: 0,
              borderWidth: 1,
              borderBottomWidth: 0,
              borderColor,
              backgroundColor: bg,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              overflow: "hidden",
              ...(clipPath ? ({ clipPath } as Record<string, unknown>) : null),
              shadowColor: "#000",
              shadowOpacity: isSelected ? 0.05 : 0,
              shadowRadius: isSelected ? 12 : 0,
              shadowOffset: { width: 0, height: -2 },
              transform: [
                {
                  translateY: isSelected ? 0 : isHovered ? -3 : 0,
                },
              ],
              ...(Platform.OS === "web"
                ? { transition: "all 160ms cubic-bezier(0.2,0,0.2,1)" }
                : null),
              opacity: pressed ? 0.86 : 1,
              zIndex: isSelected ? steps.length + 1 : steps.length - index,
            })}
          >
            {isCompleted ? (
              <Animated.View
                key={`${step.id}-done`}
                entering={ZoomIn.duration(260)}
              >
                <Check size={11} color={textColor} strokeWidth={2.4} />
              </Animated.View>
            ) : isSkipped ? (
              <SkipForward size={11} color={textColor} strokeWidth={1.8} />
            ) : (
              <Icon size={11} color={textColor} strokeWidth={1.8} />
            )}
            <Text
              className="font-dm-sans"
              style={{
                flexShrink: 1,
                minWidth: 0,
                fontSize: 10.5,
                lineHeight: 13,
                fontWeight: isSelected || isCurrent ? "800" : "700",
                color: textColor,
              }}
              numberOfLines={1}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function getBreadcrumbClipPath(index: number, count: number): string {
  const arrow = 11;
  if (count <= 1) return "";
  if (index === 0) {
    return `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%)`;
  }
  if (index === count - 1) {
    return `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrow}px 50%)`;
  }
  return `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%, ${arrow}px 50%)`;
}

function getBreadcrumbStepLabel(
  t: ReturnType<typeof useTranslations>,
  step: TreatmentStepConfig,
): string {
  switch (step.id) {
    case "pre_treatment":
      return t("care.treatment_hub.breadcrumb.prep");
    case "stimulation":
      return t("care.treatment_hub.breadcrumb.stim");
    case "protocol":
      // The "protocol" phase is ovarian stimulation for IVF/ICSI, but
      // endometrial prep (FET) or ovulation induction (OI) — those carry their
      // own shortTitle so the tab isn't mislabeled "Stim".
      return step.shortTitle ?? t("care.treatment_hub.breadcrumb.stim");
    case "trigger_shot":
      return t("care.treatment_hub.breadcrumb.trigger");
    case "egg_retrieval":
      return t("care.treatment_hub.breadcrumb.retr");
    case "freezing":
      return t("care.treatment_hub.breadcrumb.outcome");
    case "fertilization":
      return t("care.treatment_hub.breadcrumb.fert");
    case "embryo_development":
      return t("care.treatment_hub.breadcrumb.emb");
    case "transfer":
      return t("care.treatment_hub.breadcrumb.transfer");
    case "luteal_phase":
      // The luteal phase used to double as the terminal "Outcome" tab, so it was
      // labelled for the result rather than for itself. The outcome has its own
      // step now, and this tab is what it always held: the support medications.
      return t("care.treatment_hub.breadcrumb.luteal");
    case "outcome":
      return t("care.treatment_hub.breadcrumb.outcome");
    case "sperm_collection":
      return t("care.treatment_hub.breadcrumb.sperm");
    case "insemination":
      return t("care.treatment_hub.breadcrumb.iui");
    case "screening":
      return t("care.treatment_hub.breadcrumb.screen");
    case "analysis":
      return t("care.treatment_hub.breadcrumb.analysis");
    default:
      return step.shortTitle ?? step.title;
  }
}

/** Pull the "HH:MM" component out of an ISO date-time stored in step data. */
const TRANSFER_STEP_ID = "transfer";

/**
 * How a step whose record the clinic owns explains itself.
 *
 * Only the transfer can reach this today — it is the one step read out of a
 * ledger a clinic also writes to. A future clinic-owned step names itself here
 * rather than inheriting a line about a transfer.
 */
function getClinicRecordedNote(
  stepId: string,
  t: ReturnType<typeof useTranslations>,
): string {
  return stepId === TRANSFER_STEP_ID
    ? t("care.treatment_hub.your_clinic_recorded_this_transfer")
    : t("care.treatment_hub.your_clinic_recorded_this_step");
}

/** A count she actually entered, as opposed to an empty or unparsable field. */
function isUsableCount(raw: string | number | boolean | null | undefined) {
  if (typeof raw === "number") return Number.isFinite(raw);
  if (typeof raw !== "string" || raw.trim() === "") return false;
  return Number.isFinite(Number(raw));
}

function extractTime(iso: string | null): string | null {
  if (!iso) return null;
  const match = /T(\d{2}:\d{2})/.exec(iso);
  return match ? match[1]! : null;
}

/**
 * Shown above the FET transfer form when the cycle was started from the
 * CryoVault. Names the picked embryos so she can see which vault embryos this
 * transfer is for without leaving the hub.
 */
function FrozenEmbryoTransferBadge({ labels }: { labels: readonly string[] }) {
  const t = useTranslations();
  return (
    <View style={{ marginTop: 12 }}>
      <ActionBanner
        variant="info"
        icon={Snowflake}
        message={t("care.treatment_hub.fet_transfer.from_cryo_vault_note", {
          params: { count: String(labels.length), labels: labels.join(", ") },
        })}
      />
    </View>
  );
}

function MoonHubSelectedStepPanel({
  cycleContext,
  details,
  perspective,
  partnerDisplayName,
  nextPendingStep,
  frozenEmbryoLabelsForTransfer,
  onSelectStep,
  onCloseCycle,
  closeCycleLabel,
  renderPanelExtra,
  onComplete,
  onSkip,
  onUndo,
  onUpdateData,
  linkedIuiSpecimenId,
  onAddSpecimen,
  onAddMedication,
  onAddTriggerShot,
  onStartEditing,
  onScheduleProcedure,
  onRecordProcedureOutcome,
  onAddResults,
  onEditMedication,
  todayMedicationLogs,
  medicationLogDate,
  medicationLogTimezone,
}: {
  cycleContext: CycleContentContext;
  details: SelectedStepDetails;
  perspective: StepPerspective;
  partnerDisplayName?: string;
  nextPendingStep: TreatmentStepConfig | null;
  frozenEmbryoLabelsForTransfer?: readonly string[];
  onSelectStep: (stepId: string) => void;
  /** Absent for a partner, who reads the cycle but does not end it. */
  onCloseCycle?: () => void;
  closeCycleLabel?: string;
  renderPanelExtra?: (stepId: string) => ReactNode;
  onComplete: (
    stepId: string,
    data?: Record<string, string | number | boolean | null>,
  ) => void;
  onSkip: (stepId: string) => void;
  onUndo: (stepId: string) => void;
  onUpdateData: (
    stepId: string,
    data: Record<string, string | number | boolean | null>,
  ) => void;
  linkedIuiSpecimenId: string | null;
  onAddSpecimen?: () => void;
  onAddMedication: (stepId: string) => void;
  onAddTriggerShot?: (stepId: string) => void;
  onStartEditing: (stepId: string) => void;
  onScheduleProcedure?: (stepId: string) => void;
  onRecordProcedureOutcome?: (stepId: string) => void;
  onAddResults?: (stepId: string) => void;
  onEditMedication?: (med: MedicationScheduleItem) => void;
  todayMedicationLogs?: MedicationLog[];
  medicationLogDate: string;
  medicationLogTimezone: string;
}) {
  const t = useTranslations();
  const {
    step,
    stepProgress,
    partnerSourced,
    isCurrent,
    isCompleted,
    isSkipped,
    isPassedUnfilled,
    isScheduled,
    scheduledFor,
    hasPlannedData,
    timingLabel,
  } = details;
  const Icon = getMoonHubStepIcon(step);
  const stepIllustration = getStepIllustration(step.id);
  const renderLinkageAction =
    cycleContext.cycleId && step.id === "insemination" && onAddSpecimen
      ? () => (
          <IuiSpecimenLinkage
            cycleId={cycleContext.cycleId!}
            linkedSpecimenId={linkedIuiSpecimenId}
            onOpenAddSpecimen={onAddSpecimen}
            onLinkSpecimen={(specimen) =>
              onUpdateData(step.id, {
                ...(stepProgress?.data ?? {}),
                semenSpecimenId: specimen.id,
              })
            }
          />
        )
      : undefined;
  // The user can flip the panel to "edit mode" via the pencil in the
  // scheduled card or via the due-today CTA. Reset whenever the step
  // changes or the status moves out of "scheduled" (a successful save
  // either bumps it to completed or keeps it scheduled with a new date,
  // both of which warrant exiting edit mode).
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  // Reset edit-mode whenever the step changes OR the step's saved state
  // changes (status or scheduled date). The scheduledFor dep is critical:
  // re-scheduling to a new future date keeps status="scheduled" so watching
  // status alone would never fire on repeat edits.
  useEffect(() => {
    setIsEditingSchedule(false);
  }, [step.id, stepProgress?.status, stepProgress?.scheduledFor]);

  // Up Next peek: shown whenever we're viewing a step that isn't the
  // current one. Points at the current step (which by construction is the
  // first pending step in the journey) so the patient can always get back
  // to "where they are" in one tap. When viewing the current step itself,
  // we hide the peek — the primary action CTA already drives momentum.
  const showUpNext =
    !isCurrent && nextPendingStep !== null && nextPendingStep.id !== step.id;

  return (
    <CardSurface
      style={{
        // Fuse with the roadmap tab strip above: flat top, no top border, so
        // the active step's tab reads as the top edge of this card.
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderTopWidth: 0,
        padding: 16,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        {stepIllustration ? (
          <Image
            source={stepIllustration.source}
            resizeMode="contain"
            // Sits to the left of the title, spanning the title + subtitle
            // height so it reads as the step's visual anchor.
            style={{ width: 46, height: 44 }}
          />
        ) : (
          <View style={{ width: 22, alignItems: "center", paddingTop: 1 }}>
            <Icon size={17} color={TEXT_LIGHT} strokeWidth={1.75} />
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 7,
              flexWrap: "wrap",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 16,
                lineHeight: 20,
                fontWeight: "800",
                color: TEXT,
              }}
            >
              {step.title}
            </Text>
            {/* No "Coming up" badge — the estimated date range already tells
                the patient when an upcoming step is expected. */}
            {isCompleted ||
            isSkipped ||
            isCurrent ||
            isPassedUnfilled ||
            hasPlannedData ? (
              <StatusBadge
                label={
                  isCompleted
                    ? t("care.treatment_hub.status.done")
                    : isSkipped
                      ? t("care.treatment_hub.status.skipped")
                      : isCurrent
                        ? t("care.treatment_hub.status.here")
                        : isPassedUnfilled
                          ? t("care.treatment_hub.status.not_filled_in")
                          : t("care.treatment_hub.status.planned")
                }
                tone={
                  isCompleted
                    ? "green"
                    : isCurrent
                      ? "accent"
                      : isPassedUnfilled
                        ? "neutral"
                        : hasPlannedData
                          ? "blue"
                          : "neutral"
                }
              />
            ) : null}
            {partnerSourced ? (
              <StatusBadge
                label={
                  partnerDisplayName
                    ? t("care.treatment_hub.partner_name_data", {
                        params: { name: partnerDisplayName },
                      })
                    : t("care.treatment_hub.partner_data")
                }
                tone="neutral"
              />
            ) : null}
            {timingLabel ? (
              <StatusBadge label={timingLabel} tone="neutral" />
            ) : null}
          </View>
          {step.description ? (
            <Text
              className="font-dm-sans"
              style={{
                marginTop: 4,
                fontSize: 12.5,
                lineHeight: 18,
                fontWeight: "500",
                color: TEXT_LIGHT,
              }}
            >
              {step.description}
            </Text>
          ) : null}
        </View>
      </View>

      {step.id === "transfer" &&
      cycleContext.cycleType === "fet" &&
      frozenEmbryoLabelsForTransfer &&
      frozenEmbryoLabelsForTransfer.length > 0 ? (
        <FrozenEmbryoTransferBadge labels={frozenEmbryoLabelsForTransfer} />
      ) : null}

      <View style={{ marginTop: 14 }}>
        {partnerSourced ? (
          <ReadOnlyPartnerState
            details={details}
            onEditMedication={onEditMedication}
          />
        ) : isCompleted && stepProgress ? (
          <CompletedStepState
            details={details}
            onStartEditing={onStartEditing}
            renderLinkageAction={renderLinkageAction}
            onEditMedication={onEditMedication}
          />
        ) : isSkipped ? (
          <SkippedStepState step={step} onUndo={onUndo} />
        ) : isScheduled && scheduledFor && !isEditingSchedule ? (
          <>
            <MoonScheduledStepView
              step={step}
              scheduledFor={scheduledFor}
              scheduledTime={extractTime(
                (stepProgress?.data?.procedureDate as string | undefined) ??
                  (stepProgress?.data?.transferDate as string | undefined) ??
                  (stepProgress?.data?.collectionDate as string | undefined) ??
                  null,
              )}
              onEditSchedule={() => setIsEditingSchedule(true)}
              onLogResults={() => setIsEditingSchedule(true)}
            />
            {/* Once the scheduled day has arrived, the appointment tile above
                is context — the outcome form belongs on the same view so the
                patient doesn't have to tap through to log what happened. */}
            {scheduledFor.slice(0, 10) <= medicationLogDate ? (
              <View style={{ marginTop: 12 }}>
                <StepActionState
                  details={details}
                  onComplete={onComplete}
                  onSkip={onSkip}
                  onUndo={onUndo}
                  onUpdateData={onUpdateData}
                  onAddMedication={onAddMedication}
                  onAddTriggerShot={onAddTriggerShot}
                  onScheduleProcedure={onScheduleProcedure}
                  onRecordProcedureOutcome={
                    step.id === "egg_retrieval" &&
                    cycleContext.cycleType !== "egg_freezing"
                      ? undefined
                      : onRecordProcedureOutcome
                  }
                  onAddResults={onAddResults}
                  renderLinkageAction={renderLinkageAction}
                  onEditMedication={onEditMedication}
                  todayMedicationLogs={todayMedicationLogs}
                  medicationLogDate={medicationLogDate}
                  medicationLogTimezone={medicationLogTimezone}
                />
              </View>
            ) : null}
          </>
        ) : (
          // Every step keeps its action regardless of the journey pointer:
          // medication steps → add medication; schedulable procedures → a
          // "Schedule" form (date only, until it's happened); other data-capture
          // steps (PGT, screening, outcome) → their form. The advance/skip CTAs
          // are tuned per step in StepActionState.
          <StepActionState
            details={details}
            onComplete={onComplete}
            onSkip={onSkip}
            onUndo={onUndo}
            onUpdateData={onUpdateData}
            onAddMedication={onAddMedication}
            onAddTriggerShot={onAddTriggerShot}
            onScheduleProcedure={onScheduleProcedure}
            // Only egg_freezing has a canonical outcome surface (Cryo Vault
            // add-specimen). For IVF/ICSI/FET, the retrieval outcome is
            // captured inline through the step's fields (`eggsCollected`
            // etc.), so we hide the outcome row and let the wizard render
            // its own form. `outcome` keeps its beta-result surface.
            onRecordProcedureOutcome={
              step.id === "egg_retrieval" &&
              cycleContext.cycleType !== "egg_freezing"
                ? undefined
                : onRecordProcedureOutcome
            }
            onAddResults={onAddResults}
            renderLinkageAction={renderLinkageAction}
            onEditMedication={onEditMedication}
            todayMedicationLogs={todayMedicationLogs}
            medicationLogDate={medicationLogDate}
            medicationLogTimezone={medicationLogTimezone}
          />
        )}
      </View>

      {/* Host content for this step — her cycle's own data, so it sits above
          the guidance, which is the same for everyone. The panel has padding but
          no gap, and the guidance brings its own top padding, so this has to
          carry its own or it butts flush against the action rows. */}
      {renderPanelExtra ? (
        <View style={{ marginTop: 14 }}>{renderPanelExtra(step.id)}</View>
      ) : null}

      {/**
       * Closing the cycle, from the journey's last step.
       *
       * It used to live only on the full completion view, which renders when
       * every step she owns is completed or skipped — a state most cycles never
       * reach, since the optional middles are rarely filled. So the act of
       * ending a cycle was effectively unreachable from the hub, and patients
       * went looking for it.
       *
       * Offered whatever the step holds: a cycle can be closed at any point, and
       * that is her decision rather than something the form grants her. It opens
       * the end-cycle dialog, which asks — for every cycle type. It used to hand
       * non-preservation types to a handler that closed them outright, with no
       * dialog at all; see `onFinishTreatment` in `CycleDetailScreen`.
       *
       * Keyed off the last step of the flow, not a step id, because the three
       * preservation journeys do not share one.
       */}
      {details.isLastStep && onCloseCycle && closeCycleLabel ? (
        <View style={{ marginTop: 14 }}>
          <CompactActionButton
            label={closeCycleLabel}
            onPress={onCloseCycle}
            trailingIcon={ChevronRight}
            variant="primary"
            minWidth={168}
          />
        </View>
      ) : null}

      <MoonHubGuidanceCarousel
        step={step}
        perspective={perspective}
        cycleContext={cycleContext}
        measureKey={`hub-${step.id}-${isCurrent ? "current" : "selected"}`}
      />

      {showUpNext && nextPendingStep ? (
        <UpNextCard
          step={nextPendingStep}
          onPress={() => onSelectStep(nextPendingStep.id)}
        />
      ) : null}
    </CardSurface>
  );
}

function UpNextCard({
  step,
  onPress,
}: {
  step: TreatmentStepConfig;
  onPress: () => void;
}) {
  const t = useTranslations();
  const Icon = getMoonHubStepIcon(step);
  return (
    <View style={{ marginTop: 14 }}>
      <DocumentRow
        title={step.title}
        subtitle={t("care.treatment_hub.continue_where_left")}
        icon={<Icon size={18} color={TEXT_MID} strokeWidth={1.8} />}
        onPress={onPress}
        showChevron
      />
    </View>
  );
}

function StepActionState({
  details,
  onComplete,
  onSkip,
  onUndo,
  onUpdateData,
  onAddMedication,
  onAddTriggerShot,
  onScheduleProcedure,
  onRecordProcedureOutcome,
  onAddResults,
  renderLinkageAction,
  onEditMedication,
  todayMedicationLogs,
  medicationLogDate,
  medicationLogTimezone,
}: {
  details: SelectedStepDetails;
  onComplete: (
    stepId: string,
    data?: Record<string, string | number | boolean | null>,
  ) => void;
  onSkip: (stepId: string) => void;
  onUndo: (stepId: string) => void;
  onUpdateData: (
    stepId: string,
    data: Record<string, string | number | boolean | null>,
  ) => void;
  onAddMedication: (stepId: string) => void;
  onAddTriggerShot?: (stepId: string) => void;
  onScheduleProcedure?: (stepId: string) => void;
  onRecordProcedureOutcome?: (stepId: string) => void;
  onAddResults?: (stepId: string) => void;
  renderLinkageAction?: () => React.ReactNode;
  onEditMedication?: (med: MedicationScheduleItem) => void;
  todayMedicationLogs?: MedicationLog[];
  medicationLogDate: string;
  medicationLogTimezone: string;
}) {
  const t = useTranslations();
  const { step, stepProgress, medications, isUpcoming } = details;
  // On a medication step the only two real choices are "add a medication" or
  // "I didn't have this phase" (skip). A generic "Next" in the empty state is
  // meaningless — there's nothing logged to advance past. So we only surface
  // the advance CTA once at least one medication exists, and once it does we
  // hide the (now self-contradictory) skip action. Adding a med still doesn't
  // change step status, so the CTA is what lets the patient move on.
  // An upcoming step is a read-only preview: the "Save planned details" CTA and
  // the "I didn't have this phase" skip only make sense on the step you're
  // actually on, so both are suppressed until the step becomes current.
  const isMedicationStep = step.contentType === "medications";
  const hasLoggedMedications = medications.length > 0;
  const hideAdvanceInEmptyState =
    isMedicationStep && !isUpcoming && !hasLoggedMedications;
  const hideSkipAfterLogging = isMedicationStep && hasLoggedMedications;
  // Only medication steps suppress their advance/skip CTA while upcoming (the
  // "Next"/"add med" affordance is enough). Procedures — schedulable or not —
  // keep their CTA so an upcoming retrieval/transfer can still be Scheduled and
  // PGT/screening results still logged, regardless of the journey pointer.
  const suppressCtasWhenUpcoming = isUpcoming && isMedicationStep;
  // A step whose progress is derived from the cycle is never advanced by hand,
  // at any point in its life — not just while upcoming. Its panel is there to
  // explain, not to collect.
  const isDerivedProgressStep = step.derivedProgress === true;
  const pendingActionLabel = isMedicationStep
    ? t("care.treatment_hub.cta.next")
    : undefined;
  // Editing a scheduled step: the form only renders inside the "pending"
  // branch of StepWizardContent, so coerce the status so the fields show.
  // The buffered data (procedureDate/transferDate) still drives the
  // Schedule vs Confirm label via isPlanningProcedure.
  const editableProgress =
    stepProgress && stepProgress.status === "scheduled"
      ? { ...stepProgress, status: "pending" as const }
      : stepProgress;
  // Schedulable procedures (egg retrieval, transfer, IUI, sperm collection)
  // hand off to the app's appointment flow via a canonical row instead of a
  // bespoke inline date/time form.
  const scheduleEntry = getProcedureSchedulingEntry(step.id);
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <>
      <StepWizardContent
        theme="light"
        hidePrompt={step.contentType === "medications" || isDerivedProgressStep}
        pendingActionLabel={pendingActionLabel}
        hidePendingCompleteAction={
          suppressCtasWhenUpcoming ||
          hideAdvanceInEmptyState ||
          isDerivedProgressStep
        }
        hideSkip={
          suppressCtasWhenUpcoming ||
          hideSkipAfterLogging ||
          isDerivedProgressStep
        }
        step={step}
        progress={editableProgress}
        // Confirm writes, on an upcoming step as much as on the current one.
        // The panel offers this action on any non-medication step —
        // `suppressCtasWhenUpcoming` hides it only for medications — so routing
        // it to `onUpdateData` meant a visible Confirm button that buffered the
        // entry and saved nothing at all. On an FET cycle that made the transfer
        // unreachable: endometrial preparation is the clinic's to do and the
        // patient never logs it, so `protocol` stays the current step and every
        // later step reads as "upcoming" forever.
        //
        // Completing out of order is not the risk it looks like. A future date
        // still lands as `scheduled` rather than `completed` — `handleCompleteStep`
        // reads the entered date and decides — so planning ahead behaves as it
        // did; only confirming something that already happened changed.
        onComplete={(data) => onComplete(step.id, data)}
        onSkip={() => onSkip(step.id)}
        onUndo={() => onUndo(step.id)}
        onUpdateData={(data) => onUpdateData(step.id, data)}
        onAddMedication={() => onAddMedication(step.id)}
        onAddTriggerShot={
          step.hasTriggerShot && onAddTriggerShot
            ? () => onAddTriggerShot(step.id)
            : undefined
        }
        linkedMedications={medications}
        renderDateTimePicker={(props) => <DateTimeFieldPicker {...props} />}
        renderMedicationCard={(med) => {
          // #3946: multiple reminder times become multiple rows, with dose
          // status looked up per time via scheduledFor.
          const scheduleItems = localMedicationToScheduleItems(med);
          return (
            <>
              {scheduleItems.map((scheduleItem) => {
                const status = getTodayDoseStatus(
                  todayMedicationLogs,
                  {
                    medicationId: med.id,
                    medicationName: scheduleItem.name,
                    scheduledFor: `${medicationLogDate}T${scheduleItem.time}:00`,
                  },
                  medicationLogDate,
                  medicationLogTimezone,
                );
                return (
                  <MedRow
                    key={scheduleItem.time}
                    item={scheduleItem}
                    onEditMedication={onEditMedication}
                    done={Boolean(status)}
                    doneStatus={status}
                  />
                );
              })}
            </>
          );
        }}
        renderMedicationEmptyState={({ onAddMedication }) => (
          <DocumentRow
            title={getMedicationEmptyTitle(t)}
            subtitle={getMedicationEmptySubtitle(t)}
            imageSource={medicationEmptyIcon}
            trailing={<Plus size={18} color={TEXT_LIGHT} strokeWidth={1.5} />}
            onPress={onAddMedication}
            showChevron={false}
          />
        )}
        renderScheduleAction={
          scheduleEntry && onScheduleProcedure
            ? () => (
                <DocumentRow
                  title={t(scheduleEntry.rowTitleKey)}
                  subtitle={t("care.treatment_hub.schedule_procedure.subtitle")}
                  imageSource={scheduleAppointmentIcon}
                  trailing={
                    <Plus size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
                  }
                  onPress={() => onScheduleProcedure(step.id)}
                  showChevron={false}
                />
              )
            : undefined
        }
        renderLinkageAction={renderLinkageAction}
        renderSpecimenField={({ value, onChange }) => (
          <FertilizationSpecimenPicker value={value} onChange={onChange} />
        )}
        renderOutcomeAction={
          (step.id === "egg_retrieval" || step.id === "outcome") &&
          onRecordProcedureOutcome
            ? () => (
                <DocumentRow
                  /* Once results are recorded, this row no longer adds anything:
                     it reopens what she already entered. Offering "add retrieval
                     results" over a filled record read as though the first entry
                     had not landed, and calling it "add more" was no better —
                     what happens behind it is a correction. */
                  title={
                    step.id === "outcome"
                      ? t("care.treatment_hub.cycle_outcome.record_title")
                      : details.outcomeResultsRecorded
                        ? t(
                            "care.treatment_hub.retrieval_outcome.edit_the_result",
                          )
                        : t("care.treatment_hub.retrieval_outcome.add_title")
                  }
                  subtitle={
                    step.id === "outcome"
                      ? t("care.treatment_hub.cycle_outcome.record_subtitle")
                      : details.outcomeResultsRecorded
                        ? t(
                            "care.treatment_hub.retrieval_outcome.edit_recorded_retrieval_result",
                          )
                        : t("care.treatment_hub.retrieval_outcome.add_subtitle")
                  }
                  imageSource={stepDetailsIcon}
                  trailing={
                    step.id !== "outcome" && details.outcomeResultsRecorded ? (
                      <Pencil size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
                    ) : (
                      <Plus size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
                    )
                  }
                  onPress={() => onRecordProcedureOutcome(step.id)}
                  showChevron={false}
                />
              )
            : undefined
        }
        renderResultsAction={
          step.resultsUpload && onAddResults
            ? () => (
                <DocumentRow
                  title={t(
                    "care.treatment_hub.results_upload.add_the_analysis_report",
                  )}
                  subtitle={t(
                    "care.treatment_hub.results_upload.upload_the_lab_report_to_this_cycle",
                  )}
                  imageSource={stepDetailsIcon}
                  trailing={
                    <Plus size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
                  }
                  onPress={() => onAddResults(step.id)}
                  showChevron={false}
                />
              )
            : undefined
        }
        renderDetailsAction={
          stepUsesDetailsModal(step.id)
            ? // A step that ALSO offers the report upload presents typing the
              // figures as the alternative to it, not as a second row to work
              // through. A pill is a different kind of affordance from a
              // document row, which is the whole point: one hands over a file,
              // the other is something she fills in herself.
              step.resultsUpload === true
              ? () => (
                  <View style={{ alignSelf: "flex-start" }}>
                    <CompactActionButton
                      label={t(
                        STEP_DETAILS_TITLE_KEY[step.id] ??
                          "care.treatment_hub.step_details.default_title",
                      )}
                      onPress={() => setDetailsOpen(true)}
                      leadingIcon={Pencil}
                      variant="secondary"
                      minWidth={118}
                    />
                  </View>
                )
              : () => (
                  <DocumentRow
                    title={t(
                      STEP_DETAILS_TITLE_KEY[step.id] ??
                        "care.treatment_hub.step_details.default_title",
                    )}
                    subtitle={t(
                      STEP_DETAILS_SUBTITLE_KEY[step.id] ??
                        "care.treatment_hub.step_details.subtitle",
                    )}
                    imageSource={stepDetailsIcon}
                    trailing={
                      <Plus size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
                    }
                    onPress={() => setDetailsOpen(true)}
                    showChevron={false}
                  />
                )
            : undefined
        }
      />
      <StepDetailsModal
        visible={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        step={step}
        progress={editableProgress}
        onComplete={(data) => onComplete(step.id, data)}
        dataPrefill={details.dataPrefill}
        bannerText={details.dataPrefillBanner ?? null}
      />
    </>
  );
}

function hasMeaningfulStepData(
  data?: Record<string, string | number | boolean | null>,
): boolean {
  if (!data) return false;
  return Object.values(data).some(
    (value) => value !== null && value !== undefined && value !== "",
  );
}

// The step/tab already names the phase (Stim, Endometrial Preparation, Trigger,
// Luteal Support…), so the add-medication row stays generic — "stimulation"
// was wrong on non-stim phases like endometrial prep / ovulation induction.
function getMedicationEmptyTitle(
  t: ReturnType<typeof useTranslations>,
): string {
  return t("care.treatment_hub.medication_empty.default_title");
}

function getMedicationEmptySubtitle(
  t: ReturnType<typeof useTranslations>,
): string {
  return t("care.treatment_hub.medication_empty.default_subtitle");
}

function CompletedStepState({
  details,
  onStartEditing,
  renderLinkageAction,
  onEditMedication,
}: {
  details: SelectedStepDetails;
  onStartEditing: (stepId: string) => void;
  renderLinkageAction?: () => React.ReactNode;
  onEditMedication?: (med: MedicationScheduleItem) => void;
}) {
  const t = useTranslations();
  if (!details.stepProgress) return null;
  return (
    <View style={{ gap: 12 }}>
      <MoonStepFilledView
        step={details.step}
        stepProgress={details.stepProgress}
        linkedMedications={details.medications}
        onEditMedication={onEditMedication}
      />
      {renderLinkageAction ? renderLinkageAction() : null}
      {/* No Edit on a record the clinic owns. The save path refuses to write
          over it — see `isPatientOwnedFETProcedure` — so offering the form would
          take her entry and hand back the centre's figures without a word. The
          note only appears in that case: her own entries carry no provenance
          line, because "you recorded this" is not news to the person who did. */}
      {details.clinicRecorded ? (
        <View
          style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}
        >
          <View style={{ paddingTop: 2 }}>
            <Lock size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12.5,
              lineHeight: 18,
              color: TEXT_LIGHT,
              fontWeight: "500",
            }}
          >
            {getClinicRecordedNote(details.step.id, t)}
          </Text>
        </View>
      ) : (
        <View style={{ alignSelf: "flex-start" }}>
          <CompactActionButton
            label={t("care.treatment_hub.cta.edit")}
            onPress={() => onStartEditing(details.step.id)}
            leadingIcon={Pencil}
            variant="secondary"
            minWidth={118}
          />
        </View>
      )}
    </View>
  );
}

function SkippedStepState({
  step,
  onUndo,
}: {
  step: TreatmentStepConfig;
  onUndo: (stepId: string) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 12 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        }}
      >
        <SkipForward size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 12.5,
            lineHeight: 18,
            color: TEXT_LIGHT,
            fontWeight: "500",
          }}
        >
          {t("care.treatment_hub.skipped_message")}
        </Text>
      </View>
      <View style={{ alignSelf: "flex-start" }}>
        <CompactActionButton
          label={t("care.treatment_hub.cta.change")}
          onPress={() => onUndo(step.id)}
          leadingIcon={RotateCcw}
          variant="secondary"
          minWidth={118}
        />
      </View>
    </View>
  );
}

function ReadOnlyPartnerState({
  details,
  onEditMedication,
}: {
  details: SelectedStepDetails;
  onEditMedication?: (med: MedicationScheduleItem) => void;
}) {
  const t = useTranslations();
  if (details.stepProgress) {
    return (
      <MoonStepFilledView
        step={details.step}
        stepProgress={details.stepProgress}
        linkedMedications={details.medications}
        onEditMedication={onEditMedication}
      />
    );
  }
  return (
    <Text
      className="font-dm-sans"
      style={{
        fontSize: 12.5,
        lineHeight: 18,
        color: TEXT_LIGHT,
        fontWeight: "500",
      }}
    >
      {t("care.treatment_hub.waiting_partner")}
    </Text>
  );
}

function localMedicationToScheduleItems(
  m: LocalMedication,
): MedicationScheduleItem[] {
  // #3946: emit one row per scheduled time. See localMedToScheduleItems in
  // MoonStepFilledView for the matching change.
  const dose = m.dose != null ? `${m.dose} ${m.doseUnit ?? ""}`.trim() : "";
  const intakeType = m.intakeType?.toLowerCase() ?? "";
  const form =
    intakeType.includes("inject") ||
    intakeType.includes("subcutaneous") ||
    intakeType.includes("intramuscular")
      ? "vial"
      : intakeType.includes("vaginal")
        ? "suppository"
        : "pill";
  const times = m.timeTaken && m.timeTaken.length > 0 ? m.timeTaken : ["09:00"];

  return times.map((time) => {
    const hour = Number.parseInt(time, 10);
    return {
      type: "medication",
      id: m.id,
      name: m.name || "Unknown",
      dosage: dose,
      form,
      route: intakeType
        ? intakeType.charAt(0).toUpperCase() + intakeType.slice(1)
        : "",
      time,
      timing: hour >= 12 ? "evening" : "morning",
      color: TEXT_MID,
      recurrence: deriveRecurrence({
        startDate: m.startDate,
        endDate: m.endDate,
      }),
      brandName: m.name || undefined,
      userContext: {
        dosage: dose || undefined,
        route: intakeType || undefined,
        // The administration method she recorded, which `form` above is not:
        // that one has four values and picks an illustration. The injection
        // coach reads this so it does not ask again. See `LocalMedication.form`.
        selectedForm: m.form ?? undefined,
        startDate: m.startDate ?? undefined,
        endDate: m.endDate,
        notes: m.notes ?? undefined,
        timeTaken: m.timeTaken ?? undefined,
      },
    };
  });
}
